// LocalAgents.jsx — ai.degenden.xyz
// ─────────────────────────────────────────────────────────────────────────────
// LOCAL AI AGENTS — Your on-device AI operating system
//
// CONCEPT: Think of this as an OS for local LLMs.
//   Hardware tier → determines which models run well → models become agent brains
//   → agents execute intelligent workflows (Auto EDA, ML planning, reports)
//
// THREE SECTIONS:
//   01 HARDWARE     — tier detection (BASIC/STANDARD/POWER), install guides
//   02 MODEL LIBRARY— 23 models, 6 families, filter/sort, per-tier compatibility
//   03 AGENT ROSTER — 6 specialist agents, brain assignment, workflow pipelines
//
// INTELLIGENCE LAYER:
//   Each tier gets a different PROMPT STRATEGY per agent.
//   BASIC  = single-shot, compact, direct answers
//   STANDARD = chain-of-thought, multi-step
//   POWER  = full reasoning chains, self-reflection, parallel analysis
//
// Tier colors:   BASIC=#22d3ee  STANDARD=#a78bfa  POWER=#f97316
// Family colors: Qwen=#06b6d4  Llama=#3b82f6  Gemma=#10b981
//                Phi=#8b5cf6   Mistral=#f59e0b  DeepSeek=#ef4444
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { C } from './labs/wsShared';
// ═══════════════════════════════════════════════════════════════════════════════
// TOKENS
// ═══════════════════════════════════════════════════════════════════════════════
const D = {
  ...C,
  basic:    '#22d3ee',
  standard: '#a78bfa',
  power:    '#f97316',
  qwen:     '#06b6d4',
  llama:    '#3b82f6',
  gemma:    '#10b981',
  phi:      '#8b5cf6',
  mistral:  '#f59e0b',
  deepseek: '#ef4444',
};

// ═══════════════════════════════════════════════════════════════════════════════
// DATA — MODEL CATALOGUE
// ═══════════════════════════════════════════════════════════════════════════════
const FAMILY = {
  Qwen:     { color: D.qwen,     maker: 'Alibaba',   tagline: 'Best reasoning-per-param · multilingual · strong coding'   },
  Llama:    { color: D.llama,    maker: 'Meta',       tagline: 'Open standard · massive ecosystem · great general use'     },
  Gemma:    { color: D.gemma,    maker: 'Google',     tagline: 'Efficient · safe-by-design · strong instruction following' },
  Phi:      { color: D.phi,      maker: 'Microsoft',  tagline: 'Tiny punch · above-weight · perfect for edge deployment'   },
  Mistral:  { color: D.mistral,  maker: 'Mistral AI', tagline: 'European frontier · fast inference · 128k context'         },
  DeepSeek: { color: D.deepseek, maker: 'DeepSeek',   tagline: 'Chain-of-thought reasoning · top math and science scores'  },
};

// speed[0,1,2] = tokens/sec on basic/standard/power
// tiers = which tiers run it well  (0=basic, 1=standard, 2=power)
// ram = GB for Q4_K_M
const MODELS = [
  { id:'qwen3-0.6b',   f:'Qwen',     name:'Qwen3 0.6B',      p:'0.6B', ram:1,  quant:['Q4','Q8','F16'], speed:[22,40,110], tiers:[0,1,2], tags:['fast','edge','general'],           ollama:'qwen3:0.6b',      hf:'Qwen/Qwen3-0.6B-Instruct-GGUF',               desc:'Runs on anything. Best for quick tasks on minimal hardware.'        },
  { id:'qwen3-1.7b',   f:'Qwen',     name:'Qwen3 1.7B',      p:'1.7B', ram:2,  quant:['Q4','Q8'],       speed:[16,30,80],  tiers:[0,1,2], tags:['fast','coding','general'],         ollama:'qwen3:1.7b',      hf:'Qwen/Qwen3-1.7B-Instruct-GGUF',               desc:'Solid daily driver. Good at coding and structured Q&A.'             },
  { id:'qwen3-4b',     f:'Qwen',     name:'Qwen3 4B',        p:'4B',   ram:3,  quant:['Q4','Q8'],       speed:[11,22,62],  tiers:[0,1,2], tags:['reasoning','coding','data'],       ollama:'qwen3:4b',        hf:'Qwen/Qwen3-4B-Instruct-GGUF',                 desc:'Best-in-class 4B. Complex reasoning on basic hardware.'             },
  { id:'qwen3-8b',     f:'Qwen',     name:'Qwen3 8B',        p:'8B',   ram:6,  quant:['Q4','Q8'],       speed:[6,14,45],   tiers:[1,2],   tags:['reasoning','data','multilingual'], ollama:'qwen3:8b',        hf:'Qwen/Qwen3-8B-Instruct-GGUF',                 desc:'Full-power 8B. Strong on analysis, reasoning, and coding.'          },
  { id:'qwen3-14b',    f:'Qwen',     name:'Qwen3 14B',       p:'14B',  ram:10, quant:['Q4'],            speed:[4,9,30],    tiers:[1,2],   tags:['research','data','reasoning'],     ollama:'qwen3:14b',       hf:'Qwen/Qwen3-14B-Instruct-GGUF',                desc:'Professional-grade. Excellent for research and deep analysis.'      },
  { id:'qwen3-32b',    f:'Qwen',     name:'Qwen3 32B',       p:'32B',  ram:22, quant:['Q4'],            speed:[2,4,15],    tiers:[2],     tags:['frontier','reasoning','research'], ollama:'qwen3:32b',       hf:'Qwen/Qwen3-32B-Instruct-GGUF',                desc:'Near-GPT4 quality locally. High-RAM GPU required.'                  },
  { id:'qwen3-72b',    f:'Qwen',     name:'Qwen3 72B',       p:'72B',  ram:48, quant:['Q4'],            speed:[1,2,7],     tiers:[2],     tags:['frontier','reasoning','research'], ollama:'qwen3:72b',       hf:'Qwen/Qwen3-72B-Instruct-GGUF',                desc:'Frontier-class. Multi-GPU or 48GB+ VRAM only.'                      },
  { id:'llama3.2-1b',  f:'Llama',    name:'Llama 3.2 1B',    p:'1B',   ram:1,  quant:['Q4','Q8'],       speed:[24,42,115], tiers:[0,1,2], tags:['fast','edge','general'],           ollama:'llama3.2:1b',     hf:'meta-llama/Llama-3.2-1B-Instruct-GGUF',       desc:"Meta's smallest. Great for quick tasks, tight hardware."            },
  { id:'llama3.2-3b',  f:'Llama',    name:'Llama 3.2 3B',    p:'3B',   ram:2,  quant:['Q4','Q8'],       speed:[14,26,72],  tiers:[0,1,2], tags:['general','coding','fast'],         ollama:'llama3.2:3b',     hf:'meta-llama/Llama-3.2-3B-Instruct-GGUF',       desc:'Best open 3B. Strong instruction following, huge community.'        },
  { id:'llama3.1-8b',  f:'Llama',    name:'Llama 3.1 8B',    p:'8B',   ram:6,  quant:['Q4','Q8'],       speed:[5,12,42],   tiers:[1,2],   tags:['general','coding','reasoning'],    ollama:'llama3.1:8b',     hf:'meta-llama/Meta-Llama-3.1-8B-Instruct-GGUF',  desc:'The workhorse. Best quality/resource balance in open models.'       },
  { id:'llama3.1-70b', f:'Llama',    name:'Llama 3.1 70B',   p:'70B',  ram:45, quant:['Q4'],            speed:[1,2,6],     tiers:[2],     tags:['frontier','reasoning','research'], ollama:'llama3.1:70b',    hf:'meta-llama/Meta-Llama-3.1-70B-Instruct-GGUF', desc:'Frontier open model. Multi-GPU or 48GB+ VRAM.'                      },
  { id:'gemma3-1b',    f:'Gemma',    name:'Gemma 3 1B',      p:'1B',   ram:1,  quant:['Q4','Q8'],       speed:[25,44,118], tiers:[0,1,2], tags:['fast','edge','safe'],              ollama:'gemma3:1b',       hf:'google/gemma-3-1b-it-GGUF',                    desc:"Google's smallest. Efficient, safe, great for edge."                },
  { id:'gemma3-4b',    f:'Gemma',    name:'Gemma 3 4B',      p:'4B',   ram:3,  quant:['Q4','Q8'],       speed:[11,21,62],  tiers:[0,1,2], tags:['general','safe','coding'],         ollama:'gemma3:4b',       hf:'google/gemma-3-4b-it-GGUF',                    desc:'Solid all-rounder with excellent safety alignment.'                 },
  { id:'gemma3-12b',   f:'Gemma',    name:'Gemma 3 12B',     p:'12B',  ram:8,  quant:['Q4'],            speed:[4,10,35],   tiers:[1,2],   tags:['reasoning','data','research'],     ollama:'gemma3:12b',      hf:'google/gemma-3-12b-it-GGUF',                   desc:'12B sweet spot. Excellent reasoning and analysis.'                  },
  { id:'gemma3-27b',   f:'Gemma',    name:'Gemma 3 27B',     p:'27B',  ram:18, quant:['Q4'],            speed:[2,5,18],    tiers:[2],     tags:['frontier','reasoning','research'], ollama:'gemma3:27b',      hf:'google/gemma-3-27b-it-GGUF',                   desc:"Google's flagship open model. Near-GPT4 performance."               },
  { id:'phi4-mini',    f:'Phi',      name:'Phi-4 Mini 3.8B', p:'3.8B', ram:3,  quant:['Q4','Q8'],       speed:[12,23,66],  tiers:[0,1,2], tags:['coding','reasoning','edge'],       ollama:'phi4-mini',       hf:'microsoft/Phi-4-mini-instruct-GGUF',            desc:'Punches far above 3.8B in coding and math tasks.'                   },
  { id:'phi4-14b',     f:'Phi',      name:'Phi-4 14B',       p:'14B',  ram:10, quant:['Q4'],            speed:[4,8,28],    tiers:[1,2],   tags:['reasoning','coding','research'],   ollama:'phi4',            hf:'microsoft/phi-4-GGUF',                          desc:'Full Phi-4. Exceptional reasoning, math and science.'               },
  { id:'mistral-7b',   f:'Mistral',  name:'Mistral 7B',      p:'7B',   ram:5,  quant:['Q4','Q8'],       speed:[6,13,44],   tiers:[1,2],   tags:['general','coding','fast'],         ollama:'mistral',         hf:'mistralai/Mistral-7B-Instruct-v0.3-GGUF',      desc:'The model that started the open-source revolution.'                 },
  { id:'nemo-12b',     f:'Mistral',  name:'Mistral Nemo 12B',p:'12B',  ram:8,  quant:['Q4'],            speed:[4,9,32],    tiers:[1,2],   tags:['general','multilingual','long'],   ollama:'mistral-nemo',    hf:'mistralai/Mistral-Nemo-Instruct-2407-GGUF',    desc:'128k context window. Multilingual powerhouse.'                      },
  { id:'mixtral-8x7b', f:'Mistral',  name:'Mixtral 8x7B',    p:'47B',  ram:28, quant:['Q4'],            speed:[2,3,12],    tiers:[2],     tags:['frontier','general','coding'],     ollama:'mixtral',         hf:'mistralai/Mixtral-8x7B-Instruct-v0.1-GGUF',   desc:'MoE: 47B params, 13B compute cost. Outstanding quality.'            },
  { id:'dsr1-1.5b',    f:'DeepSeek', name:'DeepSeek-R1 1.5B',p:'1.5B', ram:1,  quant:['Q4','Q8'],       speed:[18,33,90],  tiers:[0,1,2], tags:['reasoning','math','fast'],         ollama:'deepseek-r1:1.5b',hf:'deepseek-ai/DeepSeek-R1-Distill-Qwen-1.5B-GGUF',desc:'Reasoning model in 1.5B. Shows chain-of-thought.'                   },
  { id:'dsr1-7b',      f:'DeepSeek', name:'DeepSeek-R1 7B',  p:'7B',   ram:5,  quant:['Q4','Q8'],       speed:[6,13,42],   tiers:[1,2],   tags:['reasoning','math','research'],     ollama:'deepseek-r1:7b',  hf:'deepseek-ai/DeepSeek-R1-Distill-Llama-7B-GGUF',desc:'Distilled giant. Beats much larger models on math.'                 },
  { id:'dsr1-8b',      f:'DeepSeek', name:'DeepSeek-R1 8B',  p:'8B',   ram:6,  quant:['Q4'],            speed:[5,11,38],   tiers:[1,2],   tags:['reasoning','research','data'],     ollama:'deepseek-r1:8b',  hf:'deepseek-ai/DeepSeek-R1-Distill-Llama-8B-GGUF',desc:'Best open reasoning in 8B class. Strong on data.'                   },
];

// ═══════════════════════════════════════════════════════════════════════════════
// DATA — AGENTS
// ═══════════════════════════════════════════════════════════════════════════════
// v1 FOCUS: Data Scientist Agent (active end-to-end).
// ML Engineer + Research Analyst shown as COMING SOON (locked).
// All other agents commented out below — uncomment to re-enable.
const AGENTS = [
  // ── ACTIVE ────────────────────────────────────────────────────────────────
  {
    id:'data_scientist', icon:'🔬', name:'Data Scientist', color:'#22d3ee',
    role:'End-to-End Data Science · Feature Engineering · Model Evaluation',
    desc:'Full-stack data scientist that runs the entire ML lifecycle — exploration, cleaning, feature engineering, model selection, evaluation and deployment readiness. Thinks in distributions, not just means.',
    pipeline:['Ingest & Profile','Quality Audit','EDA Deep Dive','Feature Engineering','Model Baseline','Eval Report'],
    rec:['qwen3-14b','dsr1-8b','phi4-14b'],
    bestTags:['data','reasoning','research'],
    caps:{ basic:'Stats + null audit', standard:'Full EDA + feature hints', power:'Research-grade analysis + feature strategy' },
    prompt:{
      basic:   'Analyse this dataset. Report column types, null%, top 3 findings. Be concise.',
      standard:'Full EDA. Per column: type, stats, nulls, outliers. Write 5 actionable data quality insights plus feature engineering candidates.',
      power:   'Chain-of-thought data science sprint.\nStep 1: Schema — types, cardinality, red flags.\nStep 2: Per-column stats with IQR + Z-score outlier detection.\nStep 3: Correlation matrix — top 10 Pearson |r| pairs.\nStep 4: Distribution shapes — flag non-normality, bimodality, heavy tails.\nStep 5: Feature engineering candidates — interactions, polynomial, log transforms.\nStep 6: Data quality score 0-100 with remediation roadmap.\nShow all reasoning before each conclusion.',
    },
  },
  // ── COMING SOON (visible but locked) ──────────────────────────────────────
  {
    id:'ml', icon:'⚙️', name:'ML Engineer', color:'#a78bfa', comingSoon: true,
    role:'Model Selection · Hyperparameter Plans · Training Strategy',
    desc:'Looks at your data and recommends the right ML algorithms, hyperparameter starting points, validation strategy, and a step-by-step training plan with success metrics.',
    pipeline:['Read EDA','Identify Task','Select Algorithms','Hyperparameter Plan','Write Strategy'],
    rec:['qwen3-14b','phi4-14b','dsr1-8b'],
    bestTags:['reasoning','coding','data'],
    caps:{ basic:'Algorithm suggestions', standard:'Full training plan', power:'AutoML-level strategy' },
    prompt:{
      basic:   'Given this dataset description, recommend 2 ML algorithms and their key hyperparameters. Be brief.',
      standard:'Analyse the ML task. Recommend 3 algorithms with pros/cons. Suggest hyperparameter ranges. Outline a cross-validation strategy.',
      power:   'Perform ML strategy planning using chain-of-thought.\nStep 1: Task classification (regression / classification / clustering) with confidence.\nStep 2: Algorithm candidates — list 5 with complexity, assumptions, and risk analysis.\nStep 3: Hyperparameter search spaces with recommended starting points.\nStep 4: Cross-validation design — folds, stratification, evaluation metrics.\nStep 5: Success criteria and failure modes.\nStep 6: Data preprocessing requirements per algorithm.\nShow full reasoning for each recommendation.',
    },
  },
  {
    id:'research', icon:'🧪', name:'Research Analyst', color:'#f59e0b', comingSoon: true,
    role:'Paper Summaries · Hypothesis Generation · Literature Context',
    desc:'Accelerates research workflows. Summarises papers, generates hypotheses from data patterns, suggests related work, structures research questions, and helps plan experiments.',
    pipeline:['Define Topic','Gather Context','Synthesise','Generate Hypotheses','Draft Outline'],
    rec:['qwen3-14b','dsr1-8b','phi4-14b'],
    bestTags:['reasoning','research','multilingual'],
    caps:{ basic:'Simple Q&A', standard:'Hypothesis + context', power:'Full research synthesis' },
    prompt:{
      basic:   'Summarise this research finding. What does it mean? What should we investigate next?',
      standard:'Analyse this finding. Generate 3 testable hypotheses. Suggest related areas to investigate. Identify potential confounders.',
      power:   'Perform structured research analysis.\nStep 1: Contextualise finding in existing knowledge — what does this confirm or contradict?\nStep 2: Generate 5 ranked hypotheses with underlying reasoning and testability scores.\nStep 3: Design validation experiments for the top 2 hypotheses.\nStep 4: Identify assumptions, limitations, and confounders.\nStep 5: Map findings to potential applications and future research directions.\nProvide citations to related areas where relevant.',
    },
  },
];
/* ── Agents coming later — paste back into AGENTS array to re-enable ───────────
  { id:'eda',            icon:'🔬', name:'Data Analyst',       color:'#22d3ee' },
  { id:'report',         icon:'📝', name:'Report Writer',      color:'#34d399' },
  { id:'code',           icon:'💻', name:'Code Reviewer',      color:'#60a5fa' },
  { id:'insight',        icon:'🎯', name:'Insight Finder',     color:'#f472b6' },
  { id:'business_analyst',icon:'📊',name:'Business Analyst',   color:'#f59e0b' },
  { id:'ml_researcher',  icon:'⚙️', name:'ML Researcher',      color:'#a78bfa' },
  { id:'vision_agent',   icon:'👁️', name:'Vision Agent',       color:'#34d399' },
  { id:'paper_eda_agent',icon:'🧪', name:'Research Paper Agent',color:'#60a5fa' },
─────────────────────────────────────────────────────────────────────────────── */

// ═══════════════════════════════════════════════════════════════════════════════
// WORKFLOWS
// ═══════════════════════════════════════════════════════════════════════════════
const WORKFLOWS = [
  { id:'eda_full',  name:'Full EDA Pipeline',        color:'#22d3ee', icon:'🔬', agents:['eda','insight','report'],                    desc:'Upload data → profile → surface insights → generate polished report'   },
  { id:'ml_ready',  name:'ML Readiness',              color:'#a78bfa', icon:'⚙️', agents:['eda','ml','code'],                            desc:'Profile data → algorithm selection → scaffold clean training code'      },
  { id:'research',  name:'Research Sprint',           color:'#f59e0b', icon:'🧪', agents:['research_asst','insight','report'],           desc:'Hypothesis → evidence → synthesis → publishable draft'                  },
  { id:'audit',     name:'Data Audit',                color:'#ef4444', icon:'🛡',  agents:['eda','code','report'],                        desc:'Quality check → issue detection → remediation report'                  },
  { id:'ds_full',   name:'Full Data Science Sprint',  color:'#22d3ee', icon:'🔬', agents:['data_scientist','ml_engineer','biz_analyst'], desc:'Raw data → EDA → ML strategy → stakeholder brief'                      },
  { id:'vision_ml', name:'Vision ML Pipeline',        color:'#34d399', icon:'👁️', agents:['vision_agent','ml_engineer'],                desc:'Visual data → CV architecture → training plan → eval harness'          },
];

// ═══════════════════════════════════════════════════════════════════════════════
// TIERS
// ═══════════════════════════════════════════════════════════════════════════════
const TIERS = [
  {
    id:0, key:'basic', label:'BASIC', icon:'🖥', color:D.basic,
    headline:'CPU Only',
    spec:'4–8 GB RAM · Any modern CPU · No GPU needed',
    detail:'Runs on any laptop made after 2018. Models up to 4B parameters. Fast enough for real daily tasks.',
    aiStyle:'Single-shot compact prompts. Direct answers only. No chain-of-thought.',
    speedRange:'8–24 tok/s',
    rec:['qwen3-0.6b','qwen3-1.7b','qwen3-4b','llama3.2-3b','gemma3-4b','phi4-mini','dsr1-1.5b'],
  },
  {
    id:1, key:'standard', label:'STANDARD', icon:'⚡', color:D.standard,
    headline:'Better CPU + RAM or iGPU',
    spec:'8–16 GB RAM · Good CPU or integrated GPU',
    detail:'Modern workstation or gaming laptop. Handles 7–14B models well. Professional data science workflows.',
    aiStyle:'Chain-of-thought enabled. Multi-step reasoning. Moderate context window usage.',
    speedRange:'5–15 tok/s',
    rec:['qwen3-8b','qwen3-14b','llama3.1-8b','gemma3-12b','phi4-14b','mistral-7b','dsr1-7b'],
  },
  {
    id:2, key:'power', label:'POWER', icon:'🔥', color:D.power,
    headline:'Dedicated GPU',
    spec:'8–24 GB VRAM · RTX 3080+ / M2 Pro+ / A-series',
    detail:'High-end workstation or Apple Silicon Pro/Max. Full 8–32B models at real-time speed. Research grade.',
    aiStyle:'Full multi-step reasoning chains. Self-reflection loops. Long context. Parallel analysis.',
    speedRange:'12–50 tok/s',
    rec:['qwen3-32b','llama3.1-8b','gemma3-27b','phi4-14b','mixtral-8x7b','dsr1-8b','nemo-12b'],
  },
];

function detectTier() {
  try {
    const t = navigator.hardwareConcurrency || 4;
    const m = navigator.deviceMemory || 4;
    if (t >= 12 && m >= 16) return 2;
    if (t >= 6  && m >= 8)  return 1;
    return 0;
  } catch { return 0; }
}

// ═══════════════════════════════════════════════════════════════════════════════
// ATOMS
// ═══════════════════════════════════════════════════════════════════════════════
const Tag = ({ label, color = D.cyan, size = 8 }) => (
  <span style={{ fontSize: size, padding: '2px 7px', background: `${color}16`, border: `1px solid ${color}38`, borderRadius: 2, color, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, whiteSpace: 'nowrap', letterSpacing: '.03em' }}>{label}</span>
);

const SectionLabel = ({ n, label, color, right }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
    <div style={{ height: 1, width: 20, background: color, flexShrink: 0 }} />
    <span style={{ fontSize: 8.5, color, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, letterSpacing: '.22em', whiteSpace: 'nowrap' }}>{n} · {label}</span>
    <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg,${color}44,transparent)` }} />
    {right && <span style={{ fontSize: 8.5, color: D.t4, fontFamily: 'JetBrains Mono,monospace', whiteSpace: 'nowrap' }}>{right}</span>}
  </div>
);

const StatusDot = ({ status }) => {
  const C2 = { offline: [D.t4, 'OFFLINE'], ready: [D.cyan, 'READY'], armed: [D.violet, 'ARMED'], running: [D.green, 'RUNNING'] };
  const [color, label] = C2[status] || [D.t4, status];
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
      <div style={{ width: 7, height: 7, borderRadius: '50%', background: color, boxShadow: status !== 'offline' ? `0 0 8px ${color}` : 'none' }} />
      <span style={{ fontSize: 7.5, color, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, letterSpacing: '.12em' }}>{label}</span>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 1 — HARDWARE
// ═══════════════════════════════════════════════════════════════════════════════
function HardwareSection({ tier, setTier }) {
  const detected = useMemo(detectTier, []);
  const T = TIERS[tier];

  return (
    <div style={{ marginBottom: 52 }}>
      <SectionLabel n="01" label="HARDWARE PROFILE" color={T.color}
        right={detected === tier ? '● AUTO-DETECTED' : '◎ MANUALLY SET'} />

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 16 }}>
        {TIERS.map(t => {
          const active = tier === t.id;
          return (
            <button key={t.id} onClick={() => setTier(t.id)}
              style={{ padding: '20px', background: active ? `${t.color}0c` : D.surface, border: `1px solid ${active ? t.color : D.b1}`, borderRadius: 6, cursor: 'pointer', textAlign: 'left', transition: 'all .18s', position: 'relative', overflow: 'hidden', outline: 'none' }}>
              {active && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg,transparent,${t.color},transparent)` }} />}
              {active && <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(ellipse at 20% 20%,${t.color}07,transparent 65%)`, pointerEvents: 'none' }} />}

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 12 }}>
                <span style={{ fontSize: 26, lineHeight: 1 }}>{t.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 900, fontSize: 18, color: active ? t.color : D.t1, letterSpacing: '.03em' }}>{t.label}</div>
                  <div style={{ fontSize: 9.5, color: active ? t.color : D.t3, fontWeight: 600 }}>{t.headline}</div>
                </div>
                {t.id === detected && <Tag label="YOUR SYSTEM" color={t.color} />}
              </div>

              <div style={{ fontSize: 8.5, color: D.t4, fontFamily: 'JetBrains Mono,monospace', marginBottom: 8 }}>{t.spec}</div>
              <div style={{ fontSize: 10, color: active ? D.t2 : D.t4, lineHeight: 1.65, marginBottom: 12 }}>{t.detail}</div>

              {/* AI reasoning style box */}
              <div style={{ padding: '8px 10px', background: active ? `${t.color}0a` : D.raised, border: `1px solid ${active ? t.color + '28' : D.b0}`, borderRadius: 3, marginBottom: 12 }}>
                <div style={{ fontSize: 7.5, color: active ? t.color : D.t4, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, letterSpacing: '.1em', marginBottom: 3 }}>AI REASONING STYLE</div>
                <div style={{ fontSize: 9, color: active ? D.t2 : D.t4, lineHeight: 1.55 }}>{t.aiStyle}</div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Tag label={t.speedRange} color={active ? t.color : D.t4} />
                <span style={{ fontSize: 8, color: D.t4, fontFamily: 'JetBrains Mono,monospace' }}>{t.rec.length} recommended</span>
              </div>
            </button>
          );
        })}
      </div>

      {/* Install tools */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {[
          { name:'Ollama', icon:'⬡', color:D.cyan,   desc:'CLI tool. One command to run any model. Best for developers.', url:'https://ollama.ai',     install:'brew install ollama',  run:'ollama run qwen3:4b' },
          { name:'LM Studio', icon:'◈', color:D.violet, desc:'Visual desktop app. Browse, download, run models. No terminal.', url:'https://lmstudio.ai', install:'Download installer',   run:'Browse → Select → Run' },
        ].map(tool => (
          <div key={tool.name} style={{ display: 'flex', gap: 14, padding: '16px', background: D.surface, border: `1px solid ${D.b1}`, borderRadius: 5 }}>
            <span style={{ fontSize: 24, flexShrink: 0 }}>{tool.icon}</span>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
                <span style={{ fontFamily: 'Syne,sans-serif', fontWeight: 800, fontSize: 14, color: tool.color }}>{tool.name}</span>
                <Tag label="FREE & OPEN SOURCE" color={D.green} />
              </div>
              <div style={{ fontSize: 10, color: D.t3, marginBottom: 10, lineHeight: 1.6 }}>{tool.desc}</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                <code style={{ fontSize: 9, background: D.raised, padding: '4px 8px', borderRadius: 3, color: D.t2, fontFamily: 'JetBrains Mono,monospace' }}>{tool.install}</code>
                <code style={{ fontSize: 9, background: `${tool.color}10`, padding: '4px 8px', borderRadius: 3, color: tool.color, fontFamily: 'JetBrains Mono,monospace', border: `1px solid ${tool.color}28` }}>{tool.run}</code>
                <a href={tool.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 8.5, color: tool.color, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, letterSpacing: '.1em', textDecoration: 'none' }}>INSTALL ↗</a>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 2 — MODEL LIBRARY
// ═══════════════════════════════════════════════════════════════════════════════
function ModelCard({ model, tier, assigned, onAssign }) {
  const fm     = FAMILY[model.f];
  const T      = TIERS[tier];
  const compat = model.tiers.includes(tier);
  const large  = !compat && tier < Math.min(...model.tiers);
  const speed  = model.speed[tier];
  const isRec  = T.rec.includes(model.id);
  const [hover, setHover] = useState(false);

  return (
    <div
      onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ background: D.surface, border: `1px solid ${hover && compat ? fm.color + '55' : compat ? D.b1 : D.b0}`, borderTop: `2px solid ${compat ? fm.color : D.b1}`, borderRadius: 5, padding: '14px', opacity: large ? .5 : 1, transition: 'border-color .15s', position: 'relative' }}>

      {isRec && <div style={{ position: 'absolute', top: 8, right: 8 }}><Tag label={`★ ${T.label}`} color={T.color} /></div>}

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 8.5, color: fm.color, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, letterSpacing: '.1em', marginBottom: 2 }}>{model.f.toUpperCase()} · {fm.maker}</div>
          <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 900, fontSize: 15, color: compat ? D.t1 : D.t3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{model.name}</div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontFamily: 'JetBrains Mono,monospace', fontWeight: 900, fontSize: 15, color: fm.color }}>{model.p}</div>
          <div style={{ fontSize: 7.5, color: D.t4, fontFamily: 'JetBrains Mono,monospace' }}>{model.ram}GB RAM</div>
        </div>
      </div>

      <div style={{ fontSize: 9.5, color: D.t3, lineHeight: 1.6, marginBottom: 9 }}>{model.desc}</div>

      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 9 }}>
        {model.tags.map(t => <Tag key={t} label={t} color={fm.color} />)}
        {model.quant.map(q => <Tag key={q} label={q} color={D.t4} size={7} />)}
      </div>

      {/* Speed bar */}
      <div style={{ marginBottom: 8 }}>
        <div style={{ fontSize: 7.5, color: D.t4, fontFamily: 'JetBrains Mono,monospace', marginBottom: 3 }}>SPEED ON {T.label} · {speed} tok/s</div>
        <div style={{ height: 3, background: D.b1, borderRadius: 2 }}>
          <div style={{ width: `${Math.min(speed / 50, 1) * 100}%`, height: '100%', background: `linear-gradient(90deg,${fm.color}77,${fm.color})`, borderRadius: 2, transition: 'width .35s' }} />
        </div>
      </div>

      <div style={{ fontSize: 8.5, fontFamily: 'JetBrains Mono,monospace', marginBottom: 11, color: compat ? D.green : large ? D.red : D.gold }}>
        {compat ? '✓ Runs well on your tier' : large ? '✗ Too large for your tier' : '⚠ Needs higher tier'}
      </div>

      {assigned.length > 0 && (
        <div style={{ marginBottom: 10, padding: '5px 8px', background: `${D.violet}10`, border: `1px solid ${D.violet}28`, borderRadius: 3 }}>
          <span style={{ fontSize: 8, color: D.violet, fontFamily: 'JetBrains Mono,monospace' }}>🧠 Brain of: {assigned.join(', ')}</span>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 5 }}>
        <a href={`https://ollama.ai/library/${model.ollama.split(':')[0]}`} target="_blank" rel="noopener noreferrer"
          style={{ padding: '8px 4px', background: `${fm.color}10`, border: `1px solid ${fm.color}35`, borderRadius: 3, color: fm.color, fontFamily: 'JetBrains Mono,monospace', fontSize: 8, fontWeight: 700, textAlign: 'center', textDecoration: 'none', display: 'block' }}>↓ OLLAMA</a>
        <a href={`https://huggingface.co/${model.hf}`} target="_blank" rel="noopener noreferrer"
          style={{ padding: '8px 4px', background: D.raised, border: `1px solid ${D.b2}`, borderRadius: 3, color: D.t3, fontFamily: 'JetBrains Mono,monospace', fontSize: 8, fontWeight: 700, textAlign: 'center', textDecoration: 'none', display: 'block' }}>↗ HF GGUF</a>
        <button onClick={() => onAssign(model.id)}
          style={{ padding: '8px 4px', background: D.b1, border: `1px solid ${D.b2}`, borderRadius: 3, color: D.t3, fontFamily: 'JetBrains Mono,monospace', fontSize: 8, fontWeight: 700, cursor: 'pointer', transition: 'all .12s' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = D.violet; e.currentTarget.style.color = D.violet; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = D.b2;     e.currentTarget.style.color = D.t3;    }}>
          🧠 ASSIGN
        </button>
      </div>
    </div>
  );
}

function ModelLibrary({ tier, agentBrains, onAssign }) {
  const [fam,    setFam]    = useState('ALL');
  const [tag,    setTag]    = useState('ALL');
  const [search, setSearch] = useState('');
  const [compat, setCompat] = useState(false);
  const [sort,   setSort]   = useState('rec');

  const allTags = useMemo(() => ['ALL', ...new Set(MODELS.flatMap(m => m.tags))], []);

  const shown = useMemo(() => {
    let ms = [...MODELS];
    if (search.trim()) ms = ms.filter(m => `${m.name} ${m.tags.join(' ')} ${m.desc}`.toLowerCase().includes(search.toLowerCase()));
    if (fam !== 'ALL')  ms = ms.filter(m => m.f === fam);
    if (tag !== 'ALL')  ms = ms.filter(m => m.tags.includes(tag));
    if (compat)         ms = ms.filter(m => m.tiers.includes(tier));
    const rec = TIERS[tier].rec;
    if (sort === 'rec')   ms = [...ms].sort((a, b) => rec.includes(b.id) - rec.includes(a.id));
    if (sort === 'speed') ms = [...ms].sort((a, b) => b.speed[tier] - a.speed[tier]);
    if (sort === 'ram')   ms = [...ms].sort((a, b) => a.ram - b.ram);
    return ms;
  }, [fam, tag, search, compat, sort, tier]);

  const assignedNames = (modelId) =>
    Object.entries(agentBrains).filter(([, m]) => m === modelId).map(([a]) => AGENTS.find(ag => ag.id === a)?.name).filter(Boolean);

  return (
    <div style={{ marginBottom: 52 }}>
      <SectionLabel n="02" label="MODEL LIBRARY" color={D.violet}
        right={`${MODELS.length} models · ${Object.keys(FAMILY).length} families · ${MODELS.filter(m => m.tiers.includes(tier)).length} compatible`} />

      {/* Family legend */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
        {['ALL', ...Object.keys(FAMILY)].map(f => {
          const fm = FAMILY[f];
          const active = fam === f;
          return (
            <button key={f} onClick={() => setFam(active && f !== 'ALL' ? 'ALL' : f)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: active && fm ? `${fm.color}14` : D.surface, border: `1px solid ${active && fm ? fm.color : D.b1}`, borderRadius: 3, cursor: 'pointer', transition: 'all .14s', outline: 'none' }}>
              {fm && <div style={{ width: 7, height: 7, borderRadius: '50%', background: fm.color, flexShrink: 0 }} />}
              <span style={{ fontSize: 9.5, color: active && fm ? fm.color : D.t3, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>{f}</span>
              {fm && <span style={{ fontSize: 8, color: D.t4 }}>{fm.maker}</span>}
            </button>
          );
        })}
      </div>

      {/* Family tagline */}
      {fam !== 'ALL' && FAMILY[fam] && (
        <div style={{ padding: '9px 14px', background: `${FAMILY[fam].color}07`, border: `1px solid ${FAMILY[fam].color}22`, borderRadius: 4, marginBottom: 14, fontSize: 10, color: D.t2 }}>
          <strong style={{ color: FAMILY[fam].color }}>{fam}</strong> · {FAMILY[fam].maker} — {FAMILY[fam].tagline}
        </div>
      )}

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16, padding: '10px 14px', background: D.deep, border: `1px solid ${D.b0}`, borderRadius: 4 }}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search models, tags, descriptions…"
          style={{ background: D.surface, border: `1px solid ${D.b2}`, color: D.t1, padding: '7px 11px', fontSize: 10, fontFamily: 'JetBrains Mono,monospace', borderRadius: 3, outline: 'none', width: 220 }} />
        <select value={tag} onChange={e => setTag(e.target.value)}
          style={{ background: D.surface, border: `1px solid ${D.b2}`, color: D.t1, padding: '7px 9px', fontSize: 10, fontFamily: 'JetBrains Mono,monospace', borderRadius: 3, outline: 'none', cursor: 'pointer' }}>
          {allTags.map(t => <option key={t} value={t}>{t === 'ALL' ? 'All use-cases' : t}</option>)}
        </select>
        <select value={sort} onChange={e => setSort(e.target.value)}
          style={{ background: D.surface, border: `1px solid ${D.b2}`, color: D.t1, padding: '7px 9px', fontSize: 10, fontFamily: 'JetBrains Mono,monospace', borderRadius: 3, outline: 'none', cursor: 'pointer' }}>
          <option value="rec">Recommended first</option>
          <option value="speed">Fastest first</option>
          <option value="ram">Lightest (RAM) first</option>
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer', fontSize: 10, color: D.t3, fontFamily: 'JetBrains Mono,monospace', userSelect: 'none' }}>
          <input type="checkbox" checked={compat} onChange={e => setCompat(e.target.checked)} style={{ accentColor: TIERS[tier].color }} />
          {TIERS[tier].label} compatible only
        </label>
        <span style={{ marginLeft: 'auto', fontSize: 8.5, color: D.t4, fontFamily: 'JetBrains Mono,monospace' }}>{shown.length} models</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(280px,1fr))', gap: 10 }}>
        {shown.map(m => <ModelCard key={m.id} model={m} tier={tier} assigned={assignedNames(m.id)} onAssign={onAssign} />)}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// BRAIN ASSIGN MODAL
// ═══════════════════════════════════════════════════════════════════════════════
function BrainModal({ modelId, agentBrains, onAssign, onClose }) {
  const model = MODELS.find(m => m.id === modelId);
  if (!model) return null;
  const fm = FAMILY[model.f];
  return (
    <div onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(2,5,8,.92)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: D.surface, border: `1px solid ${D.b2}`, borderTop: `3px solid ${fm.color}`, borderRadius: 6, width: 500, maxWidth: '96vw', boxShadow: '0 40px 100px rgba(0,0,0,.8)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', background: D.deep, borderBottom: `1px solid ${D.b0}` }}>
          <div>
            <div style={{ fontSize: 8, color: fm.color, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, letterSpacing: '.15em', marginBottom: 3 }}>ASSIGN AS AGENT BRAIN</div>
            <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 800, fontSize: 16, color: D.t1 }}>{model.name}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: D.t3, cursor: 'pointer', fontSize: 24, lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: '18px' }}>
          <div style={{ fontSize: 10, color: D.t3, marginBottom: 14, lineHeight: 1.65 }}>
            Choose which agent(s) should use <strong style={{ color: D.t1 }}>{model.name}</strong> as their reasoning engine. Agents marked as a good fit are highlighted.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {AGENTS.map(ag => {
              const isAssigned = agentBrains[ag.id] === modelId;
              const isFit = ag.rec.includes(modelId) || model.tags.some(t => ag.bestTags.includes(t));
              return (
                <button key={ag.id} onClick={() => onAssign(ag.id, isAssigned ? null : modelId)}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: isAssigned ? `${ag.color}10` : D.raised, border: `1px solid ${isAssigned ? ag.color : D.b1}`, borderRadius: 4, cursor: 'pointer', textAlign: 'left', transition: 'all .14s', outline: 'none' }}>
                  <span style={{ fontSize: 20 }}>{ag.icon}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, color: isAssigned ? ag.color : D.t1, fontWeight: 600, marginBottom: 2 }}>{ag.name}</div>
                    <div style={{ fontSize: 8.5, color: D.t4 }}>{ag.role}</div>
                  </div>
                  {isFit && !isAssigned && <Tag label="✓ GOOD FIT" color={D.green} />}
                  {isAssigned && <Tag label="● ACTIVE" color={ag.color} />}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PROMPT VIEWER MODAL
// ═══════════════════════════════════════════════════════════════════════════════
function PromptModal({ agentId, tier, onClose }) {
  const agent = AGENTS.find(a => a.id === agentId);
  if (!agent) return null;
  return (
    <div onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(2,5,8,.92)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: D.surface, border: `1px solid ${D.b2}`, borderTop: `3px solid ${agent.color}`, borderRadius: 6, width: 600, maxWidth: '96vw', maxHeight: '80vh', overflow: 'auto', boxShadow: '0 40px 100px rgba(0,0,0,.8)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', background: D.deep, borderBottom: `1px solid ${D.b0}`, position: 'sticky', top: 0 }}>
          <div>
            <div style={{ fontSize: 8, color: agent.color, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, letterSpacing: '.15em', marginBottom: 3 }}>INTELLIGENCE PROMPTS</div>
            <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 800, fontSize: 16, color: D.t1 }}>{agent.name} · All Tiers</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: D.t3, cursor: 'pointer', fontSize: 24, lineHeight: 1 }}>×</button>
        </div>
        <div style={{ padding: '18px' }}>
          <div style={{ marginBottom: 14, padding: '9px 14px', background: `${D.gold}08`, border: `1px solid ${D.gold}22`, borderRadius: 4, fontSize: 9.5, color: D.t3, lineHeight: 1.65 }}>
            💡 Prompts adapt automatically to your hardware tier. Higher tiers unlock chain-of-thought reasoning, more analytical steps, and deeper reflection. Switch tier in Section 01 to change all agent prompt strategies at once.
          </div>
          {TIERS.map(t => (
            <div key={t.id} style={{ marginBottom: 12, padding: '12px 14px', background: t.id === tier ? `${agent.color}09` : D.raised, border: `1px solid ${t.id === tier ? agent.color + '38' : D.b0}`, borderRadius: 4 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <span style={{ fontSize: 16 }}>{t.icon}</span>
                <Tag label={t.label} color={t.color} />
                <span style={{ fontSize: 8.5, color: D.t4, fontFamily: 'JetBrains Mono,monospace' }}>{t.aiStyle.split('.')[0]}</span>
                {t.id === tier && <Tag label="ACTIVE TIER" color={agent.color} size={7} />}
              </div>
              <pre style={{ margin: 0, fontSize: 9.5, color: t.id === tier ? D.t1 : D.t3, fontFamily: 'JetBrains Mono,monospace', lineHeight: 1.75, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{agent.prompt[t.key]}</pre>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// AGENT CARD
// ═══════════════════════════════════════════════════════════════════════════════
function AgentCard({ agent, tier, brain, onClear, onRun, onShowPrompt, onConfigure }) {
  const model = brain ? MODELS.find(m => m.id === brain) : null;
  const fm    = model ? FAMILY[model.f] : null;
  const T     = TIERS[tier];

  return (
    <div style={{ background: D.surface, border: `1px solid ${D.b1}`, borderLeft: `3px solid ${agent.color}`, borderRadius: 5, padding: '16px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, right: 0, width: 100, height: 100, background: `radial-gradient(circle at 100% 0%,${agent.color}05,transparent 70%)`, pointerEvents: 'none' }} />

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
        <span style={{ fontSize: 26, lineHeight: 1, flexShrink: 0 }}>{agent.icon}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 900, fontSize: 15, color: agent.color, marginBottom: 2 }}>{agent.name}</div>
          <div style={{ fontSize: 8.5, color: D.t4, fontFamily: 'JetBrains Mono,monospace', lineHeight: 1.4 }}>{agent.role}</div>
        </div>
        <StatusDot status={model ? 'armed' : 'offline'} />
      </div>

      <div style={{ fontSize: 10, color: D.t3, lineHeight: 1.7, marginBottom: 12 }}>{agent.desc}</div>

      {/* Pipeline */}
      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0, marginBottom: 12 }}>
        {agent.pipeline.map((step, i) => (
          <React.Fragment key={step}>
            <span style={{ fontSize: 7.5, color: agent.color, background: `${agent.color}0e`, border: `1px solid ${agent.color}26`, borderRadius: 2, padding: '3px 6px', fontFamily: 'JetBrains Mono,monospace', whiteSpace: 'nowrap', flexShrink: 0 }}>{step}</span>
            {i < agent.pipeline.length - 1 && <span style={{ color: D.t4, padding: '0 2px', fontSize: 9 }}>→</span>}
          </React.Fragment>
        ))}
      </div>

      {/* Tier capability + prompt link */}
      <div style={{ padding: '8px 10px', background: `${agent.color}07`, border: `1px solid ${agent.color}18`, borderRadius: 3, marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
        <div>
          <div style={{ fontSize: 7.5, color: agent.color, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, letterSpacing: '.1em', marginBottom: 3 }}>{T.label} CAPABILITY</div>
          <div style={{ fontSize: 9.5, color: D.t2 }}>{agent.caps[T.key]}</div>
        </div>
        <button onClick={() => onShowPrompt(agent.id)}
          style={{ fontSize: 8, padding: '5px 9px', background: D.b1, border: `1px solid ${D.b2}`, borderRadius: 3, color: D.t3, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0, transition: 'all .12s' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = agent.color; e.currentTarget.style.color = agent.color; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = D.b2; e.currentTarget.style.color = D.t3; }}>
          VIEW PROMPTS →
        </button>
      </div>

      {/* Brain slot */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 7.5, color: D.t4, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.1em', marginBottom: 6 }}>BRAIN MODEL</div>
        {model ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: D.raised, border: `1px solid ${fm.color}38`, borderRadius: 3 }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: fm.color, flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 11, color: fm.color, fontWeight: 700 }}>{model.name}</div>
              <div style={{ fontSize: 7.5, color: D.t4, fontFamily: 'JetBrains Mono,monospace' }}>{model.p} · {model.ram}GB RAM · {model.speed[tier]} tok/s · {model.f}</div>
            </div>
            <button onClick={onClear} style={{ fontSize: 18, background: 'none', border: 'none', color: D.t4, cursor: 'pointer', lineHeight: 1, padding: 0 }}>×</button>
          </div>
        ) : (
          <div style={{ padding: '9px 12px', background: D.raised, border: `1px dashed ${D.b2}`, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 14 }}>🧠</span>
            <span style={{ fontSize: 9.5, color: D.t4 }}>No model assigned — use Model Library to assign a brain</span>
          </div>
        )}
      </div>

      {/* Recommended models */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontSize: 7.5, color: D.t4, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.08em', marginBottom: 5 }}>RECOMMENDED MODELS</div>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          {agent.rec.map(mid => {
            const m = MODELS.find(x => x.id === mid);
            return m ? <Tag key={mid} label={m.name} color={FAMILY[m.f].color} size={8} /> : null;
          })}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
        <button onClick={() => onConfigure(agent)}
          style={{ padding: '11px', background: `${agent.color}0e`, border: `1px solid ${agent.color}40`, borderRadius: 4, color: agent.color, fontFamily: 'JetBrains Mono,monospace', fontSize: 9, fontWeight: 700, letterSpacing: '.08em', cursor: 'pointer', transition: 'all .15s' }}
          onMouseEnter={e => { e.currentTarget.style.background = `${agent.color}1c`; }}
          onMouseLeave={e => { e.currentTarget.style.background = `${agent.color}0e`; }}>
          ⚙  CONFIGURE
        </button>
        <button onClick={onRun} disabled={!model}
          style={{ padding: '11px', background: model ? `${agent.color}14` : D.b1, border: `1px solid ${model ? agent.color : D.b1}`, borderRadius: 4, color: model ? agent.color : D.t4, fontFamily: 'JetBrains Mono,monospace', fontSize: 9, fontWeight: 700, letterSpacing: '.08em', cursor: model ? 'pointer' : 'not-allowed', transition: 'all .15s' }}>
          {model ? '▶  LAUNCH' : '⊘  NO BRAIN'}
        </button>
      </div>

      {/* COMING SOON overlay */}
      {agent.comingSoon && (
        <div style={{
          position: 'absolute', inset: 0, borderRadius: 5,
          background: 'rgba(6,11,20,.88)',
          backdropFilter: 'blur(3px)',
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          gap: 10, zIndex: 10,
        }}>
          <span style={{ fontSize: 28 }}>🔒</span>
          <div style={{
            fontSize: 8.5, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700,
            letterSpacing: '.18em', color: agent.color,
            background: `${agent.color}14`, border: `1px solid ${agent.color}44`,
            padding: '5px 14px', borderRadius: 3,
          }}>COMING SOON</div>
          <div style={{ fontSize: 9.5, color: D.t4, textAlign: 'center', maxWidth: 190, lineHeight: 1.6 }}>
            {agent.name} · In development<br/>Completing Data Scientist first
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SECTION 3 — AGENT ROSTER
// ═══════════════════════════════════════════════════════════════════════════════
function AgentRoster({ tier, agentBrains, setAgentBrains, onConfigure }) {
  const [promptModal, setPromptModal] = useState(null);
  const armed = Object.values(agentBrains).filter(Boolean).length;

  return (
    <div>
      <SectionLabel n="03" label="AGENT ROSTER" color={D.power}
        right={`${armed}/${AGENTS.length} agents armed · ${TIERS[tier].label} reasoning mode`} />

      <div style={{ marginBottom: 16, padding: '10px 14px', background: `${D.cyan}07`, border: `1px solid ${D.cyan}22`, borderRadius: 4, fontSize: 9.5, color: D.t3, lineHeight: 1.65 }}>
        💡 <strong style={{ color: D.cyan }}>Tip:</strong> Assign a brain model to each agent using the Model Library above, then use the <strong style={{ color: D.t1 }}>Ollama left panel</strong> to chat with it directly — or click <strong style={{ color: D.t1 }}>LAUNCH</strong> to open an agent-scoped chat session.
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(320px,1fr))', gap: 12 }}>
        {AGENTS.map(agent => (
          <AgentCard key={agent.id} agent={agent} tier={tier}
            brain={agentBrains[agent.id] || null}
            onClear={() => setAgentBrains(p => ({ ...p, [agent.id]: null }))}
            onRun={() => {
              const brainId = agentBrains[agent.id];
              const model = brainId ? MODELS.find(m => m.id === brainId) : null;
              const ollamaTag = model?.ollama || '';
              window.dispatchEvent(new CustomEvent('ollama-launch-agent', { detail: { agent, ollamaTag } }));
            }}
            onShowPrompt={setPromptModal}
            onConfigure={onConfigure}
          />
        ))}
      </div>

      {promptModal && <PromptModal agentId={promptModal} tier={tier} onClose={() => setPromptModal(null)} />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// AGENT CONFIG STUDIO — full-screen overlay with robot character builder
// ═══════════════════════════════════════════════════════════════════════════════

// ── SVG Robot that assembles as you configure ─────────────────────────────────
function AgentRobot({ agent, brainId, personality, capabilities, temperature, memoryMode, ragEnabled, chainOfThought }) {
  const color = agent.color;
  const brain = brainId ? MODELS.find(m => m.id === brainId) : null;
  const fm    = brain ? FAMILY[brain.f] : null;
  const hasBrain = !!brain;
  const capCount = (capabilities || []).length;
  const hasPersonality = (personality || '').length > 10;
  const t = temperature ?? 0.5;

  // Glow intensity based on temperature
  const glowSize = 4 + t * 14;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0, userSelect: 'none' }}>
      <style>{`
        @keyframes robot-float { 0%,100%{transform:translateY(0px)} 50%{transform:translateY(-8px)} }
        @keyframes robot-pulse { 0%,100%{opacity:.7} 50%{opacity:1} }
        @keyframes brain-spin  { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
        @keyframes eye-blink   { 0%,90%,100%{scaleY:1} 95%{scaleY:.05} }
        @keyframes scan-h      { 0%{transform:translateY(-30px);opacity:0} 30%{opacity:.6} 70%{opacity:.6} 100%{transform:translateY(30px);opacity:0} }
        @keyframes data-flow   { 0%{stroke-dashoffset:100} 100%{stroke-dashoffset:0} }
        @keyframes particle    { 0%{transform:translateY(0) scale(1);opacity:1} 100%{transform:translateY(-40px) scale(0);opacity:0} }
        @keyframes arm-wave    { 0%,100%{transform:rotate(0deg)} 50%{transform:rotate(-15deg)} }
        @keyframes cog-spin    { 0%{transform:rotate(0deg)} 100%{transform:rotate(360deg)} }
      `}</style>

      <svg width="260" height="380" viewBox="0 0 260 380" style={{ animation: 'robot-float 4s ease-in-out infinite', filter: hasBrain ? `drop-shadow(0 0 ${glowSize}px ${color}66)` : 'none', transition: 'filter .5s' }}>
        <defs>
          <radialGradient id="bodyGrad" cx="50%" cy="40%" r="60%">
            <stop offset="0%" stopColor={hasBrain ? color + '33' : '#1e293b'} />
            <stop offset="100%" stopColor="#060b14" />
          </radialGradient>
          <radialGradient id="headGrad" cx="50%" cy="30%" r="60%">
            <stop offset="0%" stopColor={hasBrain ? color + '44' : '#1e293b'} />
            <stop offset="100%" stopColor="#0a1120" />
          </radialGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
          </filter>
          <clipPath id="headClip"><rect x="70" y="30" width="120" height="100" rx="14" /></clipPath>
        </defs>

        {/* ── ANTENNA ── */}
        <line x1="130" y1="30" x2="130" y2="10" stroke={hasBrain ? color : '#2d3f55'} strokeWidth="3" strokeLinecap="round" />
        <circle cx="130" cy="7" r="6" fill={hasBrain ? color : '#1e293b'} stroke={hasBrain ? color : '#2d3f55'} strokeWidth="1.5"
          style={{ animation: hasBrain ? 'robot-pulse 1.5s ease-in-out infinite' : 'none' }} />
        {hasBrain && <>
          <circle cx="130" cy="7" r="10" fill="none" stroke={color} strokeWidth="1" opacity=".3"
            style={{ animation: 'robot-pulse 1.5s ease-in-out infinite' }} />
          <circle cx="130" cy="7" r="15" fill="none" stroke={color} strokeWidth=".5" opacity=".15"
            style={{ animation: 'robot-pulse 2s ease-in-out infinite .3s' }} />
        </>}

        {/* ── HEAD ── */}
        <rect x="70" y="30" width="120" height="100" rx="14" fill="url(#headGrad)" stroke={hasBrain ? color : '#1e293b'} strokeWidth={hasBrain ? 1.5 : 1} />
        {/* Scan line on head when brain active */}
        {hasBrain && <rect x="70" y="30" width="120" height="4" rx="2" fill={color} opacity=".25" clipPath="url(#headClip)"
          style={{ animation: 'scan-h 2.5s linear infinite' }} />}

        {/* ── VISOR ── */}
        <rect x="82" y="48" width="96" height="30" rx="6" fill="#020508" stroke={hasBrain ? color + '60' : '#0f1a2e'} strokeWidth="1" />
        {/* Eyes */}
        {hasBrain ? <>
          {/* Active eyes — glowing */}
          <ellipse cx="106" cy="63" rx="9" ry="9" fill={fm?.color || color} opacity=".9" filter="url(#glow)" />
          <ellipse cx="154" cy="63" rx="9" ry="9" fill={fm?.color || color} opacity=".9" filter="url(#glow)" />
          <ellipse cx="106" cy="63" rx="4" ry="4" fill="#fff" opacity=".8" />
          <ellipse cx="154" cy="63" rx="4" ry="4" fill="#fff" opacity=".8" />
          {/* Pupil tracking */}
          <ellipse cx="107" cy="62" rx="2" ry="2" fill="#020508" />
          <ellipse cx="155" cy="62" rx="2" ry="2" fill="#020508" />
        </> : <>
          {/* Inactive eyes — dim */}
          <ellipse cx="106" cy="63" rx="9" ry="9" fill="#1e293b" />
          <ellipse cx="154" cy="63" rx="9" ry="9" fill="#1e293b" />
          <ellipse cx="106" cy="63" rx="3" ry="3" fill="#2d3f55" />
          <ellipse cx="154" cy="63" rx="3" ry="3" fill="#2d3f55" />
        </>}

        {/* ── MOUTH / STATUS BAR ── */}
        <rect x="95" y="94" width="70" height="14" rx="4" fill="#020508" stroke={hasPersonality ? color + '50' : '#0f1a2e'} strokeWidth="1" />
        {hasPersonality ? <>
          {/* Animated voice wave */}
          {[0,1,2,3,4,5,6].map((i) => (
            <rect key={i} x={99 + i * 9} y={101 - (i%3===0?3:i%3===1?5:2)} width="5" height={i%3===0?6:i%3===1?10:4} rx="2" fill={color} opacity=".7"
              style={{ animation: `robot-pulse ${.6 + i*.1}s ease-in-out infinite ${i*.08}s` }} />
          ))}
        </> : (
          <rect x="99" y="102" width="62" height="2" rx="1" fill="#1e293b" />
        )}

        {/* Head side vents */}
        {[0,1,2].map(i => <line key={i} x1="72" y1={50 + i * 8} x2="78" y2={50 + i * 8} stroke={color} strokeWidth="1.5" opacity={hasBrain ? '.5' : '.15'} strokeLinecap="round" />)}
        {[0,1,2].map(i => <line key={i} x1="188" y1={50 + i * 8} x2="182" y2={50 + i * 8} stroke={color} strokeWidth="1.5" opacity={hasBrain ? '.5' : '.15'} strokeLinecap="round" />)}

        {/* ── NECK ── */}
        <rect x="112" y="130" width="36" height="20" rx="4" fill="#0a1120" stroke={hasBrain ? color + '40' : '#0f1a2e'} strokeWidth="1" />
        {hasBrain && <>
          <line x1="118" y1="135" x2="142" y2="135" stroke={color} strokeWidth="1" opacity=".4" />
          <line x1="118" y1="140" x2="142" y2="140" stroke={color} strokeWidth="1" opacity=".25" />
          <line x1="118" y1="145" x2="142" y2="145" stroke={color} strokeWidth="1" opacity=".15" />
        </>}

        {/* ── TORSO ── */}
        <rect x="62" y="150" width="136" height="120" rx="12" fill="url(#bodyGrad)" stroke={hasBrain ? color + '55' : '#1e293b'} strokeWidth={hasBrain ? 1.5 : 1} />
        {/* Torso corner accents */}
        <rect x="62" y="150" width="20" height="3" rx="1.5" fill={color} opacity={hasBrain ? '.5' : '.1'} />
        <rect x="178" y="150" width="20" height="3" rx="1.5" fill={color} opacity={hasBrain ? '.5' : '.1'} />
        <rect x="62" y="267" width="20" height="3" rx="1.5" fill={color} opacity={hasBrain ? '.3' : '.05'} />
        <rect x="178" y="267" width="20" height="3" rx="1.5" fill={color} opacity={hasBrain ? '.3' : '.05'} />

        {/* ── CHEST PANEL ── */}
        <rect x="82" y="162" width="96" height="72" rx="6" fill="#020508" stroke={hasBrain ? color + '30' : '#0f1a2e'} strokeWidth="1" />

        {hasBrain ? <>
          {/* Brain model name on chest */}
          <text x="130" y="178" textAnchor="middle" fill={fm?.color || color} fontSize="7" fontFamily="JetBrains Mono,monospace" fontWeight="700" opacity=".9">
            {brain.name.toUpperCase()}
          </text>
          <text x="130" y="189" textAnchor="middle" fill={D.t4} fontSize="6" fontFamily="JetBrains Mono,monospace">
            {brain.p} · {brain.ram}GB RAM
          </text>
          {/* Animated data bars */}
          {[0,1,2,3,4].map(i => {
            const h = 6 + Math.sin(i * 1.3) * 10 + capCount * 1.5;
            return <rect key={i} x={92 + i * 16} y={230 - h} width="10" height={Math.max(4, h)} rx="2"
              fill={color} opacity={.4 + i * .1}
              style={{ animation: `robot-pulse ${.8 + i*.15}s ease-in-out infinite ${i*.1}s` }} />;
          })}
          {/* Memory mode indicator */}
          <circle cx="130" cy="215" r="8" fill="none" stroke={color} strokeWidth="1.5" opacity=".6"
            style={{ animation: memoryMode === 'persistent' ? 'robot-pulse 1s ease-in-out infinite' : 'none' }} />
          <text x="130" y="218" textAnchor="middle" fill={color} fontSize="6" fontFamily="JetBrains Mono,monospace" fontWeight="700">
            {memoryMode === 'none' ? 'OFF' : memoryMode === 'session' ? 'SES' : memoryMode === 'persistent' ? 'MEM' : 'RAG'}
          </text>
          {/* CoT indicator */}
          {chainOfThought && <>
            <line x1="87" y1="205" x2="103" y2="205" stroke={color} strokeWidth="1" opacity=".4" strokeDasharray="2,2" />
            <text x="87" y="200" fill={color} fontSize="5.5" fontFamily="JetBrains Mono,monospace" opacity=".6">CoT</text>
          </>}
          {/* Spinning cog when RAG enabled */}
          {ragEnabled && <g transform="translate(155,205)" style={{ animation: 'cog-spin 3s linear infinite', transformOrigin: '0 0' }}>
            {[0,45,90,135,180,225,270,315].map(a => {
              const rad = a * Math.PI / 180;
              return <rect key={a} x={Math.cos(rad)*7-2} y={Math.sin(rad)*7-2} width="4" height="4" rx="1" fill={D.gold} opacity=".7" transform={`rotate(${a})`} />;
            })}
            <circle cx="0" cy="0" r="4" fill={D.gold} opacity=".5" />
          </g>}
        </> : <>
          {/* Chest panel when no brain — skeleton/wireframe look */}
          <rect x="88" y="168" width="84" height="8" rx="2" fill="#0f1a2e" />
          <rect x="88" y="180" width="60" height="8" rx="2" fill="#0f1a2e" />
          <rect x="88" y="192" width="72" height="8" rx="2" fill="#0f1a2e" />
          <circle cx="130" cy="215" r="12" fill="none" stroke="#1e293b" strokeWidth="1.5" strokeDasharray="4,4" />
          <text x="130" y="218" textAnchor="middle" fill="#2d3f55" fontSize="7" fontFamily="JetBrains Mono,monospace">NO BRAIN</text>
        </>}

        {/* ── LEFT ARM ── */}
        <g style={{ transformOrigin: '62px 165px', animation: hasPersonality ? 'arm-wave 3s ease-in-out infinite' : 'none' }}>
          <rect x="34" y="155" width="28" height="88" rx="10" fill="#0a1120" stroke={hasBrain ? color + '45' : '#1e293b'} strokeWidth="1" />
          {/* Arm segments */}
          <rect x="36" y="180" width="24" height="3" rx="1.5" fill={color} opacity={hasBrain ? '.3' : '.07'} />
          <rect x="36" y="210" width="24" height="3" rx="1.5" fill={color} opacity={hasBrain ? '.2' : '.05'} />
          {/* Hand */}
          <rect x="30" y="238" width="36" height="28" rx="8" fill="#060b14" stroke={hasBrain ? color + '35' : '#0f1a2e'} strokeWidth="1" />
          {/* Finger segments */}
          {hasBrain && [0,1,2].map(i =>
            <rect key={i} x={33 + i*10} y="248" width="7" height="12" rx="3.5" fill={color} opacity=".2" />
          )}
        </g>

        {/* ── RIGHT ARM ── */}
        <g style={{ transformOrigin: '198px 165px', animation: capCount > 3 ? 'arm-wave 3.5s ease-in-out infinite .5s' : 'none' }}>
          <rect x="198" y="155" width="28" height="88" rx="10" fill="#0a1120" stroke={hasBrain ? color + '45' : '#1e293b'} strokeWidth="1" />
          <rect x="200" y="180" width="24" height="3" rx="1.5" fill={color} opacity={hasBrain ? '.3' : '.07'} />
          <rect x="200" y="210" width="24" height="3" rx="1.5" fill={color} opacity={hasBrain ? '.2' : '.05'} />
          <rect x="194" y="238" width="36" height="28" rx="8" fill="#060b14" stroke={hasBrain ? color + '35' : '#0f1a2e'} strokeWidth="1" />
          {hasBrain && [0,1,2].map(i =>
            <rect key={i} x={197 + i*10} y="248" width="7" height="12" rx="3.5" fill={color} opacity=".2" />
          )}
          {/* Tool icon on right hand if caps > 5 */}
          {capCount > 5 && <text x="212" y="256" textAnchor="middle" fontSize="14" style={{ animation: 'robot-pulse 1.5s ease-in-out infinite' }}>⚡</text>}
        </g>

        {/* ── WAIST ── */}
        <rect x="72" y="268" width="116" height="18" rx="4" fill="#060b14" stroke={hasBrain ? color + '30' : '#0f1a2e'} strokeWidth="1" />
        {hasBrain && [0,1,2,3,4].map(i =>
          <rect key={i} x={80 + i * 22} y="273" width="14" height="8" rx="2" fill={color} opacity={.1 + i*.04} />
        )}

        {/* ── LEGS ── */}
        {/* Left leg */}
        <rect x="75" y="286" width="46" height="70" rx="10" fill="#0a1120" stroke={hasBrain ? color + '40' : '#1e293b'} strokeWidth="1" />
        <rect x="77" y="316" width="42" height="3" rx="1.5" fill={color} opacity={hasBrain ? '.25' : '.06'} />
        {/* Left foot */}
        <rect x="68" y="350" width="60" height="22" rx="8" fill="#060b14" stroke={hasBrain ? color + '30' : '#0f1a2e'} strokeWidth="1" />

        {/* Right leg */}
        <rect x="139" y="286" width="46" height="70" rx="10" fill="#0a1120" stroke={hasBrain ? color + '40' : '#1e293b'} strokeWidth="1" />
        <rect x="141" y="316" width="42" height="3" rx="1.5" fill={color} opacity={hasBrain ? '.25' : '.06'} />
        {/* Right foot */}
        <rect x="132" y="350" width="60" height="22" rx="8" fill="#060b14" stroke={hasBrain ? color + '30' : '#0f1a2e'} strokeWidth="1" />

        {/* ── SHOULDER CAPS ── */}
        <ellipse cx="62" cy="158" rx="14" ry="12" fill="#0a1120" stroke={hasBrain ? color + '50' : '#1e293b'} strokeWidth="1.5" />
        <ellipse cx="198" cy="158" rx="14" ry="12" fill="#0a1120" stroke={hasBrain ? color + '50' : '#1e293b'} strokeWidth="1.5" />
        {hasBrain && <>
          <ellipse cx="62" cy="158" rx="7" ry="6" fill={color} opacity=".12" />
          <ellipse cx="198" cy="158" rx="7" ry="6" fill={color} opacity=".12" />
        </>}

        {/* ── PARTICLES when fully armed ── */}
        {hasBrain && capCount > 4 && [0,1,2,3].map(i => (
          <circle key={i} cx={90 + i * 26} cy="148" r="2.5" fill={color} opacity=".6"
            style={{ animation: `particle 1.8s ease-out infinite ${i * .4}s` }} />
        ))}
      </svg>

      {/* Status label below robot */}
      <div style={{ textAlign: 'center', marginTop: -8 }}>
        <div style={{ fontSize: 8, color: hasBrain ? color : D.t4, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, letterSpacing: '.18em', marginBottom: 4 }}>
          {hasBrain ? '● ONLINE' : '○ OFFLINE'}
        </div>
        <div style={{ fontSize: 9, color: D.t3, fontFamily: 'JetBrains Mono,monospace' }}>
          {hasBrain ? `${brain.name} · T=${(temperature ?? 0.5).toFixed(1)}` : 'Assign brain to activate'}
        </div>
      </div>
    </div>
  );
}

// ── Config helper atoms ───────────────────────────────────────────────────────
function CLabel({ children }) {
  return <div style={{ fontSize: 8, color: D.t4, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.14em', fontWeight: 700, marginBottom: 7, textTransform: 'uppercase' }}>{children}</div>;
}
const cInput = { width: '100%', background: D.deep, border: `1px solid ${D.b2}`, color: D.t1, padding: '8px 10px', fontSize: 11, fontFamily: 'system-ui,sans-serif', borderRadius: 3, outline: 'none' };

function CSlider({ label, value, min, max, step, onChange, color, unit = '' }) {
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
        <span style={{ fontSize: 9, color: D.t3, fontFamily: 'JetBrains Mono,monospace' }}>{label}</span>
        <span style={{ fontSize: 9, color, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>{value}{unit}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={e => onChange(parseFloat(e.target.value))}
        style={{ width: '100%', accentColor: color, cursor: 'pointer' }} />
    </div>
  );
}

function CToggle({ value, onChange, color, label, sublabel }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <div>
        <div style={{ fontSize: 10.5, color: D.t2 }}>{label}</div>
        {sublabel && <div style={{ fontSize: 8.5, color: D.t4 }}>{sublabel}</div>}
      </div>
      <div onClick={() => onChange(!value)}
        style={{ width: 36, height: 18, borderRadius: 9, background: value ? color : D.b2, cursor: 'pointer', position: 'relative', transition: 'background .2s', flexShrink: 0 }}>
        <div style={{ position: 'absolute', top: 2, left: value ? 19 : 2, width: 14, height: 14, borderRadius: '50%', background: D.t1, transition: 'left .18s' }} />
      </div>
    </div>
  );
}

// ── AgentConfigStudio ─────────────────────────────────────────────────────────
const ALL_CAPS = [
  { id:'statistical_analysis', label:'Stats Analysis', icon:'📊' },
  { id:'feature_engineering',  label:'Feature Eng.',   icon:'🔧' },
  { id:'model_evaluation',     label:'Model Eval',     icon:'📈' },
  { id:'code_generation',      label:'Code Gen',       icon:'💻' },
  { id:'report_generation',    label:'Reports',        icon:'📝' },
  { id:'visualization',        label:'Visualization',  icon:'📉' },
  { id:'hypothesis_testing',   label:'Hypothesis',     icon:'🎯' },
  { id:'literature_synthesis', label:'Literature',     icon:'📚' },
  { id:'experiment_design',    label:'Experiments',    icon:'🧬' },
  { id:'causal_inference',     label:'Causal Inf.',    icon:'🔗' },
  { id:'nlp_processing',       label:'NLP',            icon:'🔤' },
  { id:'image_analysis',       label:'Image Analysis', icon:'👁️' },
  { id:'time_series',          label:'Time Series',    icon:'📅' },
  { id:'bayesian_modeling',    label:'Bayesian',       icon:'🎲' },
  { id:'ab_testing',           label:'A/B Testing',    icon:'⚖️' },
  { id:'anomaly_detection',    label:'Anomaly Detect', icon:'🚨' },
];
const MEMORY_OPTS = [
  { id:'none', label:'No Memory', desc:'Stateless — fresh every run' },
  { id:'session', label:'Session', desc:'Remembers within one session' },
  { id:'persistent', label:'Persistent', desc:'Long-term knowledge base' },
  { id:'rag', label:'RAG + Memory', desc:'Dynamic document retrieval' },
];
const OUTPUT_OPTS = ['structured_report','executive_brief','technical_spec','research_draft','json','markdown','plain_text'];
const PERSONALITY_PRESETS = [
  { id:'rigorous',   label:'Rigorous Scientist',   desc:'Evidence-first, quantifies uncertainty' },
  { id:'pragmatic',  label:'Pragmatic Engineer',   desc:'Shipping-focused, scalability-aware' },
  { id:'creative',   label:'Creative Researcher',  desc:'Lateral thinking, explores edge cases' },
  { id:'teacher',    label:'Clear Explainer',       desc:'Analogies, breaks down complexity' },
  { id:'contrarian', label:"Devil's Advocate",      desc:'Challenges assumptions, stress-tests ideas' },
];
const ACCENT_COLORS = ['#22d3ee','#a78bfa','#f97316','#34d399','#60a5fa','#f472b6','#fb923c','#ef4444','#fbbf24','#4ade80'];

function AgentConfigStudio({ agent, tier, agentBrains, setAgentBrains, onClose }) {
  const T = TIERS[tier];

  // Tabs
  const [tab, setTab] = useState('brain');

  // State — identity
  const [name, setName]   = useState(agent.name);
  const [emoji, setEmoji] = useState(agent.icon);
  const [color, setColor] = useState(agent.color);

  // State — brain
  const [brainId, setBrainId] = useState(agentBrains[agent.id] || null);

  // State — personality
  const [personality, setPersonality]     = useState(agent.desc || '');
  const [personalityPreset, setPersonalityPreset] = useState('rigorous');
  const [goals, setGoals]                 = useState('');
  const [customInstr, setCustomInstr]     = useState('');

  // State — capabilities
  const [caps, setCaps] = useState(agent.bestTags?.map(t => t) || []);

  // State — rag
  const [ragEnabled, setRagEnabled]   = useState(false);
  const [ragSources, setRagSources]   = useState('');
  const [ragChunkSize, setRagChunkSize] = useState(512);
  const [ragTopK, setRagTopK]         = useState(5);

  // State — behaviour
  const [memoryMode, setMemoryMode]   = useState('session');
  const [temperature, setTemperature] = useState(0.4);
  const [maxTokens, setMaxTokens]     = useState(2048);
  const [chainOfThought, setChainOfThought] = useState(true);
  const [selfReflect, setSelfReflect] = useState(false);
  const [outputFormat, setOutputFormat] = useState('structured_report');

  // State — prompts
  const [sysPrompt, setSysPrompt] = useState(agent.prompt?.[T.key] || '');

  const brain = brainId ? MODELS.find(m => m.id === brainId) : null;
  const fm    = brain ? FAMILY[brain.f] : null;

  const toggleCap = id => setCaps(p => p.includes(id) ? p.filter(c => c !== id) : [...p, id]);

  function handleDeploy() {
    setAgentBrains(p => ({ ...p, [agent.id]: brainId }));
    // Persist to localStorage so ChatContext / chatStore can read the config
    try {
      const brainModel = MODELS.find(m => m.id === brainId);
      // brainOllamaTag is the actual Ollama model tag (e.g. 'qwen3:4b') used
      // by chatStore to call the API and check model availability.
      const brainOllamaTag = brainModel?.ollama || brainId;
      const brainName      = brainModel?.name   || brainId;
      const stored = JSON.parse(localStorage.getItem('agentConfigs') || '{}');
      stored[agent.id] = {
        name, icon: emoji, color,
        brainId,                  // internal MODELS[] id  (kept for reference)
        brainOllamaTag,           // actual Ollama tag      (used for API calls)
        brainName,                // human-readable name    (shown in chat header)
        personality, personalityCustom: personality,   // accept both key names
        personalityPreset, goals, customInstr, caps,
        ragEnabled, ragSources, ragChunkSize, ragTopK,
        memoryMode, temperature, maxTokens,
        chainOfThought, selfReflect, outputFormat,
        sysPrompt,                // LocalAgents key
        systemPrompt: sysPrompt,  // chatStore key alias
      };
      localStorage.setItem('agentConfigs', JSON.stringify(stored));
      // Notify ChatContext to re-probe Ollama and refresh model list
      window.dispatchEvent(new CustomEvent('dd:agent:configured'));
    } catch(_) {}
    onClose();
  }

  const TABS = [
    { id:'brain',        label:'🧠 Brain' },
    { id:'identity',     label:'🪪 Identity' },
    { id:'personality',  label:'🎭 Personality' },
    { id:'capabilities', label:'⚡ Capabilities' },
    { id:'rag',          label:'📚 RAG' },
    { id:'behaviour',    label:'⚙️ Behaviour' },
    { id:'prompts',      label:'🎯 Prompts' },
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(2,5,8,.97)', zIndex: 3000, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <style>{`@keyframes studio-in{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}`}</style>

      {/* ── TOP BAR ── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 20px', borderBottom: `1px solid ${D.b0}`, background: D.deep, flexShrink: 0 }}>
        <button onClick={onClose} style={{ background: 'none', border: `1px solid ${D.b2}`, color: D.t4, cursor: 'pointer', padding: '5px 11px', borderRadius: 3, fontFamily: 'JetBrains Mono,monospace', fontSize: 8.5, fontWeight: 700, letterSpacing: '.1em' }}>← BACK</button>
        <div style={{ fontSize: 7.5, color: D.t4, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.22em' }}>AGENT CONFIGURATION STUDIO</div>
        <div style={{ flex: 1, height: 1, background: `linear-gradient(90deg,${D.b1},transparent)` }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontFamily: 'Syne,sans-serif', fontWeight: 900, color: D.t1 }}>
          <span>{emoji}</span>
          <span style={{ background: `linear-gradient(90deg,${color},#a78bfa)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{name}</span>
        </div>
        <button onClick={handleDeploy}
          style={{ padding: '9px 20px', background: `${color}18`, border: `1px solid ${color}`, borderRadius: 4, color, fontFamily: 'JetBrains Mono,monospace', fontSize: 9.5, fontWeight: 700, cursor: 'pointer', letterSpacing: '.08em', transition: 'all .15s' }}>
          ▶ DEPLOY AGENT
        </button>
      </div>

      {/* ── THREE-PANE LAYOUT ── */}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* LEFT — tab nav */}
        <div style={{ width: 160, borderRight: `1px solid ${D.b0}`, background: D.deep, display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{ padding: '11px 14px', background: tab === t.id ? `${color}12` : 'transparent', border: 'none', borderLeft: `2px solid ${tab === t.id ? color : 'transparent'}`, cursor: 'pointer', textAlign: 'left', fontSize: 10.5, color: tab === t.id ? color : D.t3, fontWeight: tab === t.id ? 700 : 400, transition: 'all .13s' }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* CENTRE — config panels */}
        <div style={{ flex: 1, overflow: 'auto', padding: '24px 28px', animation: 'studio-in .2s ease' }} key={tab}>

          {/* ── BRAIN ── */}
          {tab === 'brain' && (
            <div>
              <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 900, fontSize: 18, color, marginBottom: 4 }}>Brain Model</div>
              <div style={{ fontSize: 10.5, color: D.t3, marginBottom: 20 }}>Choose the LLM that powers this agent's reasoning engine</div>

              {brain && (
                <div style={{ padding: '14px 16px', background: `${fm.color}0a`, border: `1.5px solid ${fm.color}44`, borderRadius: 6, marginBottom: 18, display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                  <div style={{ width: 40, height: 40, borderRadius: 6, background: `${fm.color}18`, border: `1px solid ${fm.color}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, flexShrink: 0 }}>🧠</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 900, fontSize: 16, color: fm.color, marginBottom: 2 }}>{brain.name}</div>
                    <div style={{ fontSize: 8.5, color: D.t4, fontFamily: 'JetBrains Mono,monospace' }}>{brain.f} · {brain.p} · {brain.ram}GB RAM · {brain.speed[tier]} tok/s on {T.label}</div>
                    <div style={{ fontSize: 10, color: D.t3, marginTop: 5, lineHeight: 1.6 }}>{brain.desc}</div>
                  </div>
                  <button onClick={() => setBrainId(null)} style={{ background: 'none', border: `1px solid ${D.b2}`, color: D.t4, cursor: 'pointer', padding: '4px 7px', borderRadius: 3, fontSize: 12 }}>×</button>
                </div>
              )}

              <CLabel>Recommended for {agent.name}</CLabel>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 7, marginBottom: 18 }}>
                {agent.rec.map(mid => {
                  const m = MODELS.find(x => x.id === mid); if (!m) return null;
                  const mfm = FAMILY[m.f]; const sel = brainId === mid;
                  return (
                    <button key={mid} onClick={() => setBrainId(mid)}
                      style={{ padding: '11px 12px', background: sel ? `${mfm.color}12` : D.surface, border: `1px solid ${sel ? mfm.color : D.b1}`, borderRadius: 4, cursor: 'pointer', textAlign: 'left', outline: 'none', transition: 'all .13s' }}>
                      <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 800, fontSize: 12, color: sel ? mfm.color : D.t1, marginBottom: 3, display: 'flex', justifyContent: 'space-between' }}>
                        {m.name} {sel && <Tag label="● ACTIVE" color={mfm.color} size={7} />}
                      </div>
                      <div style={{ fontSize: 8, color: D.t4, fontFamily: 'JetBrains Mono,monospace' }}>{m.p} · {m.ram}GB · {m.speed[tier]} tok/s</div>
                    </button>
                  );
                })}
              </div>

              <details>
                <summary style={{ cursor: 'pointer', fontSize: 9, color: D.t3, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, letterSpacing: '.1em', padding: '7px 0' }}>▶ ALL COMPATIBLE MODELS ({MODELS.filter(m => m.tiers.includes(tier)).length})</summary>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 5, marginTop: 8, maxHeight: 260, overflow: 'auto' }}>
                  {MODELS.filter(m => m.tiers.includes(tier)).map(m => {
                    const mfm = FAMILY[m.f]; const sel = brainId === m.id;
                    return (
                      <button key={m.id} onClick={() => setBrainId(m.id)}
                        style={{ padding: '8px 10px', background: sel ? `${mfm.color}12` : D.raised, border: `1px solid ${sel ? mfm.color : D.b0}`, borderRadius: 3, cursor: 'pointer', textAlign: 'left', outline: 'none' }}>
                        <div style={{ fontSize: 10, color: sel ? mfm.color : D.t2, fontWeight: sel ? 700 : 400 }}>{m.name}</div>
                        <div style={{ fontSize: 7.5, color: D.t4, fontFamily: 'JetBrains Mono,monospace', marginTop: 2 }}>{m.p} · {m.ram}GB</div>
                      </button>
                    );
                  })}
                </div>
              </details>
            </div>
          )}

          {/* ── IDENTITY ── */}
          {tab === 'identity' && (
            <div>
              <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 900, fontSize: 18, color, marginBottom: 4 }}>Agent Identity</div>
              <div style={{ fontSize: 10.5, color: D.t3, marginBottom: 20 }}>Name, appearance and role</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 14, marginBottom: 16 }}>
                <div><CLabel>Name</CLabel><input value={name} onChange={e => setName(e.target.value)} style={cInput} /></div>
                <div><CLabel>Icon</CLabel><input value={emoji} onChange={e => setEmoji(e.target.value)} style={{ ...cInput, width: 70 }} /></div>
              </div>
              <div style={{ marginBottom: 16 }}><CLabel>Accent Color</CLabel>
                <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                  {ACCENT_COLORS.map(c => (
                    <button key={c} onClick={() => setColor(c)}
                      style={{ width: 30, height: 30, borderRadius: '50%', background: c, border: color === c ? `3px solid ${D.t1}` : '2px solid transparent', cursor: 'pointer', outline: 'none', boxShadow: color === c ? `0 0 10px ${c}99` : 'none', transition: 'all .14s' }} />
                  ))}
                </div>
              </div>
              <div style={{ padding: '12px 14px', background: D.surface, border: `1px solid ${color}18`, borderRadius: 4 }}>
                <CLabel>Pipeline</CLabel>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 0 }}>
                  {agent.pipeline.map((s, i) => (
                    <React.Fragment key={s}>
                      <span style={{ fontSize: 7.5, color, background: `${color}0e`, border: `1px solid ${color}26`, borderRadius: 2, padding: '3px 7px', fontFamily: 'JetBrains Mono,monospace' }}>{s}</span>
                      {i < agent.pipeline.length - 1 && <span style={{ color: D.t4, padding: '0 3px', fontSize: 9, alignSelf: 'center' }}>→</span>}
                    </React.Fragment>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── PERSONALITY ── */}
          {tab === 'personality' && (
            <div>
              <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 900, fontSize: 18, color, marginBottom: 4 }}>Personality & Directives</div>
              <div style={{ fontSize: 10.5, color: D.t3, marginBottom: 20 }}>How the agent thinks, communicates and prioritises</div>
              <CLabel>Preset Character</CLabel>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(170px,1fr))', gap: 7, marginBottom: 18 }}>
                {PERSONALITY_PRESETS.map(p => (
                  <button key={p.id} onClick={() => setPersonalityPreset(p.id)}
                    style={{ padding: '10px 11px', background: personalityPreset === p.id ? `${color}10` : D.surface, border: `1px solid ${personalityPreset === p.id ? color : D.b1}`, borderRadius: 4, cursor: 'pointer', textAlign: 'left', outline: 'none' }}>
                    <div style={{ fontSize: 10.5, color: personalityPreset === p.id ? color : D.t1, fontWeight: 600, marginBottom: 3 }}>{p.label}</div>
                    <div style={{ fontSize: 8, color: D.t4, lineHeight: 1.5 }}>{p.desc}</div>
                  </button>
                ))}
              </div>
              <div style={{ marginBottom: 14 }}><CLabel>Personality Description</CLabel>
                <textarea value={personality} onChange={e => setPersonality(e.target.value)} rows={3} placeholder="Describe the agent's character and communication style..." style={{ ...cInput, resize: 'vertical', lineHeight: 1.7 }} />
              </div>
              <div style={{ marginBottom: 14 }}><CLabel>Core Goals</CLabel>
                <textarea value={goals} onChange={e => setGoals(e.target.value)} rows={3} placeholder="What does this agent optimise for?" style={{ ...cInput, resize: 'vertical', lineHeight: 1.7 }} />
              </div>
              <div><CLabel>Custom Instructions</CLabel>
                <textarea value={customInstr} onChange={e => setCustomInstr(e.target.value)} rows={2} placeholder="Always cite sources... Format output as JSON..." style={{ ...cInput, resize: 'vertical', lineHeight: 1.7 }} />
              </div>
            </div>
          )}

          {/* ── CAPABILITIES ── */}
          {tab === 'capabilities' && (
            <div>
              <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 900, fontSize: 18, color, marginBottom: 4 }}>Capabilities</div>
              <div style={{ fontSize: 10.5, color: D.t3, marginBottom: 20 }}>{caps.length} selected — toggle what this agent can do</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: 6 }}>
                {ALL_CAPS.map(cap => {
                  const on = caps.includes(cap.id);
                  return (
                    <button key={cap.id} onClick={() => toggleCap(cap.id)}
                      style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '9px 11px', background: on ? `${color}10` : D.surface, border: `1px solid ${on ? color : D.b1}`, borderRadius: 4, cursor: 'pointer', textAlign: 'left', outline: 'none', transition: 'all .12s' }}>
                      <span style={{ fontSize: 16, flexShrink: 0 }}>{cap.icon}</span>
                      <span style={{ fontSize: 10, color: on ? color : D.t2, fontWeight: on ? 700 : 400, flex: 1 }}>{cap.label}</span>
                      <div style={{ width: 12, height: 12, borderRadius: '50%', background: on ? color : 'transparent', border: `1px solid ${on ? color : D.b2}`, flexShrink: 0, transition: 'all .12s' }} />
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── RAG ── */}
          {tab === 'rag' && (
            <div>
              <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 900, fontSize: 18, color, marginBottom: 4 }}>Knowledge RAG</div>
              <div style={{ fontSize: 10.5, color: D.t3, marginBottom: 20 }}>Ground the agent in your domain documents (processed locally — never leaves your machine)</div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', background: ragEnabled ? `${color}08` : D.surface, border: `1px solid ${ragEnabled ? color + '44' : D.b1}`, borderRadius: 5, marginBottom: 18, transition: 'all .2s' }}>
                <div>
                  <div style={{ fontSize: 12.5, color: ragEnabled ? color : D.t2, fontWeight: 600, marginBottom: 2 }}>Enable RAG</div>
                  <div style={{ fontSize: 9, color: D.t4 }}>Retrieval-Augmented Generation — agent fetches relevant chunks before generating</div>
                </div>
                <div onClick={() => setRagEnabled(v => !v)}
                  style={{ width: 36, height: 18, borderRadius: 9, background: ragEnabled ? color : D.b2, cursor: 'pointer', position: 'relative', transition: 'background .2s', flexShrink: 0 }}>
                  <div style={{ position: 'absolute', top: 2, left: ragEnabled ? 19 : 2, width: 14, height: 14, borderRadius: '50%', background: D.t1, transition: 'left .18s' }} />
                </div>
              </div>
              {ragEnabled && <>
                <div style={{ marginBottom: 16 }}><CLabel>Document Sources</CLabel>
                  <textarea value={ragSources} onChange={e => setRagSources(e.target.value)} rows={4} placeholder={`Suggested for ${agent.name}:\n• domain literature PDFs\n• company data dictionary\n• past analysis reports`} style={{ ...cInput, resize: 'vertical', lineHeight: 1.7 }} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
                  <div>
                    <CSlider label="Chunk Size" value={ragChunkSize} min={128} max={2048} step={64} onChange={setRagChunkSize} color={color} unit=" tok" />
                    <div style={{ fontSize: 8, color: D.t4, marginTop: 4 }}>Smaller = precise retrieval. Larger = more context.</div>
                  </div>
                  <div>
                    <CSlider label="Top-K" value={ragTopK} min={1} max={20} step={1} onChange={setRagTopK} color={color} />
                    <div style={{ fontSize: 8, color: D.t4, marginTop: 4 }}>Chunks retrieved per query.</div>
                  </div>
                </div>
                <div style={{ marginTop: 14, padding: '10px 12px', background: D.surface, border: `1px solid ${D.b1}`, borderRadius: 4, fontSize: 9, color: D.t3, lineHeight: 1.7 }}>
                  <strong style={{ color: D.gold }}>🔒 Privacy:</strong> Only RAG config (chunk size, top-k, source descriptions) is saved to backend. Your documents stay on your machine and are never uploaded.
                </div>
              </>}
            </div>
          )}

          {/* ── BEHAVIOUR ── */}
          {tab === 'behaviour' && (
            <div>
              <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 900, fontSize: 18, color, marginBottom: 4 }}>Behaviour Parameters</div>
              <div style={{ fontSize: 10.5, color: D.t3, marginBottom: 20 }}>Control how the agent thinks and outputs</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                <div>
                  <CLabel>Memory Mode</CLabel>
                  {MEMORY_OPTS.map(m => (
                    <button key={m.id} onClick={() => setMemoryMode(m.id)}
                      style={{ display: 'flex', flexDirection: 'column', width: '100%', padding: '9px 11px', marginBottom: 5, background: memoryMode === m.id ? `${color}10` : D.surface, border: `1px solid ${memoryMode === m.id ? color : D.b1}`, borderRadius: 4, cursor: 'pointer', textAlign: 'left', outline: 'none' }}>
                      <span style={{ fontSize: 11, color: memoryMode === m.id ? color : D.t2, fontWeight: memoryMode === m.id ? 700 : 400 }}>{m.label}</span>
                      <span style={{ fontSize: 8, color: D.t4, marginTop: 2 }}>{m.desc}</span>
                    </button>
                  ))}
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  <CSlider label="Temperature" value={temperature} min={0} max={2} step={0.05} onChange={setTemperature} color={color} />
                  <div style={{ fontSize: 8.5, color: D.t4, marginTop: -10 }}>
                    {temperature <= 0.3 ? '🎯 Deterministic' : temperature <= 0.7 ? '⚖️ Balanced' : temperature <= 1.2 ? '🎨 Creative' : '🌪️ Wild'}
                  </div>
                  <CSlider label="Max Tokens" value={maxTokens} min={256} max={8192} step={256} onChange={setMaxTokens} color={color} unit=" tok" />
                  <CLabel>Output Format</CLabel>
                  <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                    {OUTPUT_OPTS.map(f => (
                      <button key={f} onClick={() => setOutputFormat(f)}
                        style={{ padding: '5px 9px', background: outputFormat === f ? `${color}16` : D.surface, border: `1px solid ${outputFormat === f ? color : D.b1}`, borderRadius: 3, cursor: 'pointer', fontSize: 8.5, color: outputFormat === f ? color : D.t3, fontFamily: 'JetBrains Mono,monospace', fontWeight: outputFormat === f ? 700 : 400, outline: 'none' }}>
                        {f.replace(/_/g, ' ')}
                      </button>
                    ))}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <CToggle value={chainOfThought} onChange={setChainOfThought} color={color} label="Chain-of-Thought" sublabel="Show reasoning steps before answer" />
                    <CToggle value={selfReflect} onChange={setSelfReflect} color={color} label="Self-Reflection Pass" sublabel="Agent critiques its own output first" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ── PROMPTS ── */}
          {tab === 'prompts' && (
            <div>
              <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 900, fontSize: 18, color, marginBottom: 4 }}>System Prompt</div>
              <div style={{ fontSize: 10.5, color: D.t3, marginBottom: 16 }}>Core instructions — tier-adaptive defaults loaded below</div>
              <div style={{ display: 'flex', gap: 5, marginBottom: 10 }}>
                {TIERS.map(t => (
                  <button key={t.id} onClick={() => setSysPrompt(agent.prompt?.[t.key] || '')}
                    style={{ padding: '5px 11px', background: D.surface, border: `1px solid ${t.color}44`, borderRadius: 3, cursor: 'pointer', fontSize: 8.5, color: t.color, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, outline: 'none' }}>
                    Load {t.label}
                  </button>
                ))}
              </div>
              <textarea value={sysPrompt} onChange={e => setSysPrompt(e.target.value)} rows={14}
                style={{ ...cInput, resize: 'vertical', lineHeight: 1.8, fontFamily: 'JetBrains Mono,monospace', fontSize: 9.5 }} />
              <div style={{ marginTop: 10, padding: '10px 12px', background: D.surface, border: `1px solid ${D.b1}`, borderRadius: 4, fontSize: 8.5, color: D.t4, fontFamily: 'JetBrains Mono,monospace' }}>
                ~{Math.ceil(sysPrompt.split(' ').length * 1.3)} prompt tokens · {maxTokens} max output · CoT {chainOfThought ? 'ON' : 'OFF'} · Reflect {selfReflect ? 'ON' : 'OFF'}
              </div>
            </div>
          )}
        </div>

        {/* RIGHT — robot character preview */}
        <div style={{ width: 320, borderLeft: `1px solid ${D.b0}`, background: D.deep, display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, overflow: 'auto', padding: '28px 16px 28px' }}>
          <div style={{ fontSize: 7.5, color: D.t4, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.2em', marginBottom: 20 }}>AGENT PREVIEW</div>

          <AgentRobot
            agent={{ ...agent, color, icon: emoji, name }}
            brainId={brainId}
            personality={personality}
            capabilities={caps}
            temperature={temperature}
            memoryMode={memoryMode}
            ragEnabled={ragEnabled}
            chainOfThought={chainOfThought}
          />

          {/* Build progress */}
          <div style={{ width: '100%', marginTop: 20 }}>
            <div style={{ fontSize: 7.5, color: D.t4, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.16em', marginBottom: 10 }}>BUILD PROGRESS</div>
            {[
              ['Brain',         !!brainId,                    '🧠'],
              ['Identity',      name !== agent.name || color !== agent.color, '🪪'],
              ['Personality',   personality.length > 10,       '🎭'],
              ['Capabilities',  caps.length > 0,               '⚡'],
              ['Knowledge RAG', ragEnabled,                    '📚'],
              ['Behaviour',     temperature !== 0.4,           '⚙️'],
              ['Prompt',        sysPrompt.length > 20,         '🎯'],
            ].map(([label, done, icon]) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
                <span style={{ fontSize: 11 }}>{icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ height: 4, background: D.b1, borderRadius: 2 }}>
                    <div style={{ width: done ? '100%' : '0%', height: '100%', background: done ? color : 'transparent', borderRadius: 2, transition: 'width .4s ease' }} />
                  </div>
                </div>
                <span style={{ fontSize: 8.5, color: done ? color : D.t4, fontFamily: 'JetBrains Mono,monospace', fontWeight: done ? 700 : 400, width: 90 }}>{label}</span>
                <span style={{ fontSize: 10 }}>{done ? '✓' : '○'}</span>
              </div>
            ))}
          </div>

          {/* Spec readout */}
          <div style={{ width: '100%', marginTop: 16, padding: '12px', background: D.surface, border: `1px solid ${D.b1}`, borderRadius: 4 }}>
            <div style={{ fontSize: 7.5, color: D.t4, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.14em', marginBottom: 8 }}>AGENT SPEC</div>
            {[
              ['MODEL',   brain ? brain.name : '—',           brain ? fm.color : D.t4],
              ['PARAMS',  brain ? brain.p : '—',              brain ? fm.color : D.t4],
              ['SPEED',   brain ? brain.speed[tier]+' tok/s' : '—', D.t2],
              ['CAPS',    caps.length + ' enabled',           color],
              ['MEMORY',  memoryMode.toUpperCase(),           color],
              ['TEMP',    temperature.toFixed(2),             temperature <= .4 ? D.basic : temperature <= .9 ? D.green : D.gold],
              ['RAG',     ragEnabled ? 'ON' : 'OFF',          ragEnabled ? D.gold : D.t4],
            ].map(([k, v, c]) => (
              <div key={k} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                <span style={{ fontSize: 8, color: D.t4, fontFamily: 'JetBrains Mono,monospace' }}>{k}</span>
                <span style={{ fontSize: 8.5, color: c, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// OLLAMA LEFT PANEL — handles all 3 user states end-to-end
// ═══════════════════════════════════════════════════════════════════════════════

const OLLAMA_BASE = 'http://localhost:11434';

// True when using a remote tunnel (ngrok, etc.) vs a local Ollama instance
const OLLAMA_IS_REMOTE = !OLLAMA_BASE.includes('localhost') && !OLLAMA_BASE.includes('127.0.0.1');

// For GET requests: append as a query param so no CORS preflight is triggered.
// Adding it as a request header would require Ollama to whitelist it in
// Access-Control-Allow-Headers — which it typically doesn't. A query param
// is treated as part of the URL and is passed through ngrok without a preflight.
const NGROK_QS = OLLAMA_IS_REMOTE ? '?ngrok-skip-browser-warning=1' : '';

// Detect if Ollama is reachable and fetch installed models
// Returns status: 'connected' | 'cors-error' | 'offline' | 'error'
async function probeOllama() {
  const ctrl = new AbortController();
  // Shorter timeout for localhost (snappier dev UX); longer for remote tunnels
  const timeoutMs = OLLAMA_IS_REMOTE ? 7000 : 3000;
  const timeout = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(`${OLLAMA_BASE}/api/tags${NGROK_QS}`, { signal: ctrl.signal });
    clearTimeout(timeout);
    if (!res.ok) return { status: 'error', models: [] };
    // Guard against ngrok HTML interstitial slipping through
    const text = await res.text();
    if (text.trimStart().startsWith('<')) return { status: 'ngrok-interstitial', models: [] };
    const data = JSON.parse(text);
    return { status: 'connected', models: data.models || [] };
  } catch (err) {
    clearTimeout(timeout);
    if (err?.name === 'AbortError') return { status: 'offline', models: [] };
    // Distinguish "server running but CORS headers missing" from "server unreachable".
    // A no-cors opaque fetch succeeds even without CORS headers; a pure network
    // error throws in both modes. This applies to both localhost and remote —
    // e.g. localhost dev server on :3000 fetching Ollama on :11434 is cross-origin.
    try {
      const nc = new AbortController();
      const nct = setTimeout(() => nc.abort(), 3000);
      await fetch(`${OLLAMA_BASE}/api/tags${NGROK_QS}`, { signal: nc.signal, mode: 'no-cors' });
      clearTimeout(nct);
      // Reached here → server is up, CORS headers just aren't set
      return { status: 'cors-error', models: [] };
    } catch { /* server truly unreachable */ }
    return { status: 'offline', models: [] };
  }
}

// Stream a chat completion from Ollama
async function* streamOllama(model, messages, signal) {
  const res = await fetch(`${OLLAMA_BASE}/api/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ model, messages, stream: true }),
    signal,
  });
  if (!res.ok) throw new Error(`Ollama responded ${res.status}`);
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const lines = buf.split('\n');
    buf = lines.pop();
    for (const line of lines) {
      if (!line.trim()) continue;
      try {
        const obj = JSON.parse(line);
        if (obj.message?.content) yield obj.message.content;
        if (obj.done) return;
      } catch { /* skip malformed */ }
    }
  }
}

// Pull a model (streaming progress)
async function* pullModel(name) {
  const res = await fetch(`${OLLAMA_BASE}/api/pull`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name, stream: true }),
  });
  if (!res.ok) throw new Error(`Pull failed: ${res.status}`);
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = '';
  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const lines = buf.split('\n');
    buf = lines.pop();
    for (const line of lines) {
      if (!line.trim()) continue;
      try { yield JSON.parse(line); } catch { /* skip */ }
    }
  }
}

// ── Visual install steps for "I don't know Ollama" ───────────────────────────
const INSTALL_OS = [
  {
    id: 'mac',
    label: 'macOS',
    icon: '\u{1F34E}',
    steps: [
      { cmd: null, desc: 'Go to ollama.com, click Download for Mac and run the .dmg installer' },
      { cmd: 'open ~/Downloads/Ollama-darwin.dmg', desc: 'Double-click the .dmg and drag Ollama to Applications' },
      { cmd: 'ollama serve', desc: 'Open Terminal and start the Ollama server (runs on port 11434)' },
      { cmd: 'ollama run qwen3:4b', desc: 'Pull and run your first model. Qwen3 4B is great for most tasks.' },
    ],
    note: 'Ollama runs as a menu-bar app on macOS. You will see the llama icon in your menu bar when it is running.',
  },
  {
    id: 'win',
    label: 'Windows',
    icon: '⊞',
    steps: [
      { cmd: null, desc: 'Go to ollama.com and download the Windows installer (.exe)' },
      { cmd: null, desc: 'Run OllamaSetup.exe — it installs in the background and starts automatically' },
      { cmd: 'ollama serve', desc: 'Open PowerShell or CMD and start the server (or it may already be running)' },
      { cmd: 'ollama run qwen3:4b', desc: 'Pull and run your first model' },
    ],
    note: 'On Windows, Ollama starts on login by default. Check the system tray for the llama icon.',
  },
  {
    id: 'linux',
    label: 'Linux',
    icon: '🐧',
    steps: [
      { cmd: 'curl -fsSL https://ollama.com/install.sh | sh', desc: 'One-command install — works on Ubuntu, Debian, Arch, and most distros' },
      { cmd: 'ollama serve', desc: 'Start the server (or use systemctl start ollama for auto-start)' },
      { cmd: 'ollama run qwen3:4b', desc: 'Pull and run your first model' },
    ],
    note: 'On Linux with NVIDIA GPU, install CUDA drivers first and Ollama will automatically use the GPU.',
  },
];

// ── Step atom — numbered step with optional sub-text and code block ─────────────
function Step({ n, text, sub, code }) {
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
      <div style={{ width: 22, height: 22, borderRadius: '50%', background: `${D.cyan}18`, border: `1px solid ${D.cyan}40`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, color: D.cyan, fontFamily: 'JetBrains Mono,monospace', fontWeight: 900, flexShrink: 0, marginTop: 1 }}>{n}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 10.5, color: D.t2, lineHeight: 1.5, marginBottom: sub || code ? 4 : 0 }}>{text}</div>
        {sub && <div style={{ fontSize: 9, color: D.t4, lineHeight: 1.6, marginBottom: code ? 4 : 0 }}>{sub}</div>}
        {code && (
          <code style={{ display: 'block', fontSize: 9, color: D.cyan, background: D.raised, padding: '5px 9px', borderRadius: 3, fontFamily: 'JetBrains Mono,monospace', wordBreak: 'break-all' }}>{code}</code>
        )}
      </div>
    </div>
  );
}

// ── CORS notice component ─────────────────────────────────────────────────────
function CorsNotice() {
  return (
    <div style={{ padding: '10px 12px', background: `${D.gold}09`, border: `1px solid ${D.gold}33`, borderRadius: 4, fontSize: 9, color: D.t3, lineHeight: 1.7 }}>
      <div style={{ color: D.gold, fontWeight: 700, fontFamily: 'JetBrains Mono,monospace', marginBottom: 4 }}>⚠ CORS required</div>
      If you see a connection error but Ollama is running, start it with CORS enabled:
      <br />
      <code style={{ display: 'block', marginTop: 5, padding: '4px 8px', background: D.raised, borderRadius: 2, color: D.cyan, fontFamily: 'JetBrains Mono,monospace', fontSize: 8.5, wordBreak: 'break-all' }}>
        OLLAMA_ORIGINS=* ollama serve
      </code>
      <div style={{ marginTop: 5, fontSize: 8.5, color: D.t4 }}>
        Or on Windows: set OLLAMA_ORIGINS=* in System Environment Variables.
      </div>
    </div>
  );
}

// ── Ollama chat panel ─────────────────────────────────────────────────────────
function OllamaChat({ installedModels, onRefresh, launchContext, onClearLaunch }) {
  const [selectedModel, setSelectedModel] = useState(
    installedModels[0]?.name || ''
  );
  const [agent, setAgent] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [err, setErr] = useState('');
  const [pullTarget, setPullTarget] = useState('');
  const [pulling, setPulling] = useState(false);
  const [pullProgress, setPullProgress] = useState('');
  const [pullErr, setPullErr] = useState('');
  const abortRef = useRef(null);
  const bottomRef = useRef(null);

  // Keep model selection in sync when list changes
  useEffect(() => {
    if (!selectedModel && installedModels[0]) setSelectedModel(installedModels[0].name);
  }, [installedModels, selectedModel]);

  // Handle LAUNCH from agent card — pre-select model + agent
  useEffect(() => {
    if (!launchContext) return;
    const { agent: launchAgent, ollamaTag } = launchContext;
    // Try to find the installed model matching the ollama tag
    const matched = installedModels.find(m =>
      m.name === ollamaTag || m.name.startsWith(ollamaTag.split(':')[0])
    );
    if (matched) setSelectedModel(matched.name);
    else if (installedModels[0]) setSelectedModel(installedModels[0].name);
    setAgent(launchAgent);
    setMessages([]);
    setErr('');
    onClearLaunch?.();
  }, [launchContext]); // eslint-disable-line

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streaming]);

  const matchedAgent = useMemo(() => {
    if (!selectedModel) return null;
    const lower = selectedModel.toLowerCase();
    return AGENTS.find(ag =>
      ag.rec.some(rid => MODELS.find(m => m.id === rid)?.ollama && lower.startsWith(MODELS.find(m => m.id === rid)?.ollama.split(':')[0]))
    ) || null;
  }, [selectedModel]);

  async function sendMessage() {
    if (!input.trim() || !selectedModel || streaming) return;
    setErr('');
    const userMsg = { role: 'user', content: input.trim() };
    const systemMsg = agent
      ? { role: 'system', content: agent.prompt?.standard || agent.desc }
      : null;
    const history = systemMsg ? [systemMsg, ...messages, userMsg] : [...messages, userMsg];

    setMessages(prev => [...prev, userMsg, { role: 'assistant', content: '', _streaming: true }]);
    setInput('');
    setStreaming(true);

    const ctrl = new AbortController();
    abortRef.current = ctrl;
    let assembled = '';
    try {
      for await (const chunk of streamOllama(selectedModel, history, ctrl.signal)) {
        assembled += chunk;
        setMessages(prev => {
          const copy = [...prev];
          copy[copy.length - 1] = { role: 'assistant', content: assembled, _streaming: true };
          return copy;
        });
      }
      setMessages(prev => {
        const copy = [...prev];
        copy[copy.length - 1] = { role: 'assistant', content: assembled };
        return copy;
      });
    } catch (ex) {
      if (ex?.name !== 'AbortError') {
        setErr(ex?.message || 'Stream error');
        setMessages(prev => prev.slice(0, -1));
      }
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  }

  async function handlePull() {
    if (!pullTarget.trim() || pulling) return;
    setPulling(true);
    setPullErr('');
    setPullProgress('Starting download...');
    try {
      for await (const evt of pullModel(pullTarget.trim())) {
        if (evt.status) {
          const pct = evt.total ? ` ${Math.round((evt.completed / evt.total) * 100)}%` : '';
          setPullProgress(`${evt.status}${pct}`);
        }
        if (evt.status === 'success') {
          setPullProgress('');
          setPullTarget('');
          onRefresh();
          break;
        }
      }
    } catch (ex) {
      setPullErr(ex?.message || 'Pull failed');
    } finally {
      setPulling(false);
    }
  }

  const modelsizeLabel = (m) => {
    const gb = m.size ? (m.size / 1e9).toFixed(1) + ' GB' : '';
    return `${m.name}${gb ? ' · ' + gb : ''}`;
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, height: '100%' }}>
      {/* Model picker */}
      <div style={{ padding: '10px 12px', borderBottom: `1px solid ${D.b0}` }}>
        <div style={{ fontSize: 8, color: D.t4, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.12em', marginBottom: 5 }}>ACTIVE MODEL</div>
        <select
          value={selectedModel}
          onChange={e => { setSelectedModel(e.target.value); setMessages([]); setErr(''); }}
          style={{ width: '100%', background: D.deep, border: `1px solid ${D.b2}`, color: D.t1, padding: '7px 8px', fontSize: 10, fontFamily: 'JetBrains Mono,monospace', borderRadius: 3, outline: 'none', cursor: 'pointer' }}
        >
          {installedModels.map(m => (
            <option key={m.name} value={m.name}>{modelsizeLabel(m)}</option>
          ))}
        </select>
        {matchedAgent && (
          <div style={{ marginTop: 6, padding: '5px 8px', background: `${matchedAgent.color}0d`, border: `1px solid ${matchedAgent.color}30`, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: 12 }}>{matchedAgent.icon}</span>
            <span style={{ fontSize: 8.5, color: matchedAgent.color, fontFamily: 'JetBrains Mono,monospace' }}>Best for: {matchedAgent.name}</span>
          </div>
        )}
      </div>

      {/* Agent quick-select */}
      <div style={{ padding: '8px 12px', borderBottom: `1px solid ${D.b0}` }}>
        <div style={{ fontSize: 8, color: D.t4, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.12em', marginBottom: 5 }}>AGENT MODE (optional)</div>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <button
            onClick={() => { setAgent(null); setMessages([]); }}
            style={{ padding: '4px 8px', background: !agent ? `${D.cyan}14` : D.surface, border: `1px solid ${!agent ? D.cyan : D.b1}`, borderRadius: 2, color: !agent ? D.cyan : D.t4, fontSize: 8, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}
          >
            Free Chat
          </button>
          {AGENTS.slice(0, 5).map(ag => (
            <button
              key={ag.id}
              onClick={() => { setAgent(agent?.id === ag.id ? null : ag); setMessages([]); }}
              style={{ padding: '4px 8px', background: agent?.id === ag.id ? `${ag.color}14` : D.surface, border: `1px solid ${agent?.id === ag.id ? ag.color : D.b1}`, borderRadius: 2, color: agent?.id === ag.id ? ag.color : D.t4, fontSize: 8, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, whiteSpace: 'nowrap' }}
            >
              {ag.icon} {ag.name.split(' ')[0]}
            </button>
          ))}
        </div>
        {agent && (
          <div style={{ marginTop: 5, fontSize: 8.5, color: D.t3, lineHeight: 1.55, padding: '5px 8px', background: D.raised, borderRadius: 3 }}>
            <span style={{ color: agent.color, fontWeight: 700 }}>{agent.name}</span> — {agent.caps?.standard}
          </div>
        )}
      </div>

      {/* Messages */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px', display: 'flex', flexDirection: 'column', gap: 10, minHeight: 0 }}>
        {messages.length === 0 && (
          <div style={{ textAlign: 'center', padding: '30px 16px', color: D.t4 }}>
            <div style={{ fontSize: 24, marginBottom: 10 }}>🧠</div>
            <div style={{ fontSize: 10, fontFamily: 'JetBrains Mono,monospace', color: D.t3, marginBottom: 6 }}>
              {agent ? `${agent.name} ready` : 'Ollama connected'}
            </div>
            <div style={{ fontSize: 9, color: D.t4, lineHeight: 1.7 }}>
              {agent
                ? `Ask me to ${agent.role.toLowerCase()}`
                : 'Ask anything — your model runs 100% locally'}
            </div>
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
            <div style={{ fontSize: 7.5, color: D.t4, fontFamily: 'JetBrains Mono,monospace', marginBottom: 3, letterSpacing: '.08em' }}>
              {msg.role === 'user' ? 'YOU' : (agent ? agent.name.toUpperCase() : selectedModel.toUpperCase())}
            </div>
            <div style={{
              maxWidth: '92%', padding: '9px 12px', borderRadius: 4,
              background: msg.role === 'user' ? `${D.cyan}12` : D.surface,
              border: `1px solid ${msg.role === 'user' ? D.cyan + '30' : D.b1}`,
              fontSize: 11, color: D.t1, lineHeight: 1.75,
              fontFamily: msg.role === 'assistant' ? 'system-ui,sans-serif' : 'inherit',
              whiteSpace: 'pre-wrap', wordBreak: 'break-word',
            }}>
              {msg.content}
              {msg._streaming && <span style={{ display: 'inline-block', width: 7, height: 11, background: D.cyan, marginLeft: 2, animation: 'robot-pulse 0.6s ease-in-out infinite', verticalAlign: 'middle', borderRadius: 1 }} />}
            </div>
          </div>
        ))}
        {err && (
          <div style={{ padding: '8px 10px', background: `${D.red}10`, border: `1px solid ${D.red}33`, borderRadius: 3, fontSize: 9.5, color: D.red }}>{err}</div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div style={{ padding: '10px 12px', borderTop: `1px solid ${D.b0}` }}>
        {streaming && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
            <button
              onClick={() => { abortRef.current?.abort(); setStreaming(false); }}
              style={{ padding: '4px 10px', background: `${D.red}12`, border: `1px solid ${D.red}44`, borderRadius: 2, color: D.red, fontSize: 8, fontFamily: 'JetBrains Mono,monospace', cursor: 'pointer', fontWeight: 700 }}
            >
              ■ STOP
            </button>
          </div>
        )}
        <div style={{ display: 'flex', gap: 6 }}>
          <textarea
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
            placeholder={streaming ? 'Streaming...' : 'Type a message (Enter to send, Shift+Enter for newline)'}
            disabled={streaming}
            rows={2}
            style={{ flex: 1, background: D.deep, border: `1px solid ${D.b2}`, color: D.t1, padding: '8px 10px', fontSize: 11, fontFamily: 'system-ui,sans-serif', borderRadius: 3, outline: 'none', resize: 'none', lineHeight: 1.5 }}
          />
          <button
            onClick={sendMessage}
            disabled={!input.trim() || streaming}
            style={{ padding: '8px 14px', background: !input.trim() || streaming ? D.b1 : `${D.cyan}18`, border: `1px solid ${!input.trim() || streaming ? D.b1 : D.cyan}`, borderRadius: 3, color: !input.trim() || streaming ? D.t4 : D.cyan, fontSize: 12, cursor: !input.trim() || streaming ? 'not-allowed' : 'pointer', fontWeight: 700, flexShrink: 0 }}
          >
            ↑
          </button>
        </div>
        <div style={{ marginTop: 8 }}>
          <details>
            <summary style={{ fontSize: 8.5, color: D.t4, fontFamily: 'JetBrains Mono,monospace', cursor: 'pointer', letterSpacing: '.08em' }}>
              ⬇ Pull new model
            </summary>
            <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
              <input
                value={pullTarget}
                onChange={e => setPullTarget(e.target.value)}
                placeholder="e.g. qwen3:4b"
                style={{ flex: 1, background: D.deep, border: `1px solid ${D.b2}`, color: D.t1, padding: '6px 8px', fontSize: 9.5, fontFamily: 'JetBrains Mono,monospace', borderRadius: 3, outline: 'none' }}
              />
              <button
                onClick={handlePull}
                disabled={!pullTarget.trim() || pulling}
                style={{ padding: '6px 12px', background: pulling ? D.b1 : `${D.violet}18`, border: `1px solid ${pulling ? D.b1 : D.violet}`, borderRadius: 3, color: pulling ? D.t4 : D.violet, fontSize: 9, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, cursor: pulling ? 'not-allowed' : 'pointer' }}
              >
                {pulling ? 'PULLING...' : 'PULL'}
              </button>
            </div>
            {pullProgress && (
              <div style={{ marginTop: 6, fontSize: 8.5, color: D.t3, fontFamily: 'JetBrains Mono,monospace', padding: '4px 8px', background: D.raised, borderRadius: 3 }}>
                {pullProgress}
              </div>
            )}
            {pullErr && <div style={{ marginTop: 4, fontSize: 9, color: D.red }}>{pullErr}</div>}
            <div style={{ marginTop: 6, fontSize: 8, color: D.t4, lineHeight: 1.65 }}>
              Supported tags: see <a href="https://ollama.com/library" target="_blank" rel="noopener noreferrer" style={{ color: D.cyan, textDecoration: 'none' }}>ollama.com/library ↗</a>
            </div>
          </details>
        </div>
      </div>
    </div>
  );
}

// ── OllamaPanel — focused state machine ─────────────────────────────────────
function OllamaPanel({ onClose }) {
  const [phase, setPhase]               = useState('checking');
  const [ollamaModels, setOllamaModels] = useState([]);
  const [os, setOs]                     = useState('mac');
  const [showInstall, setShowInstall]   = useState(false);
  const [launchContext, setLaunchContext] = useState(null);
  const [retrying, setRetrying]         = useState(false);

  // Auto-detect OS
  useEffect(() => {
    const ua = navigator.userAgent || '';
    if (ua.includes('Win')) setOs('win');
    else if (ua.includes('Linux')) setOs('linux');
    else setOs('mac');
  }, []);

  // Listen for LAUNCH events from agent cards
  useEffect(() => {
    const handler = (e) => setLaunchContext(e.detail);
    window.addEventListener('ollama-launch-agent', handler);
    return () => window.removeEventListener('ollama-launch-agent', handler);
  }, []);

  async function probe() {
    setPhase('checking');
    const result = await probeOllama();
    setOllamaModels(result.models);
    if (result.status === 'connected') setPhase('connected');
    else if (result.status === 'cors-error') setPhase('cors-error');
    else setPhase('offline');
  }

  useEffect(() => { probe(); }, []); // eslint-disable-line

  const OS_LABEL = { mac: 'macOS', win: 'Windows', linux: 'Linux' };

  // ────────────────────────────── CORS-ERROR (server running, no CORS headers)
  if (phase === 'cors-error') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto', padding: '22px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <div style={{ width: 10, height: 10, borderRadius: '50%', background: D.gold, boxShadow: `0 0 8px ${D.gold}60`, flexShrink: 0 }} />
          <div>
            <div style={{ fontSize: 15, fontWeight: 700, color: D.t1, lineHeight: 1.1 }}>Ollama is running</div>
            <div style={{ fontSize: 9, color: D.t4, marginTop: 3 }}>but it's blocking browser connections</div>
          </div>
        </div>

        <div style={{ fontSize: 10.5, color: D.t2, marginBottom: 6, fontWeight: 600 }}>One fix needed — takes 10 seconds</div>
        <div style={{ fontSize: 10, color: D.t3, marginBottom: 18, lineHeight: 1.7 }}>
          By default, Ollama only accepts direct computer connections, not browser requests.
          You need to restart it with one extra setting:
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 22 }}>
          {os !== 'win' ? (
            <Step n={1} text="Stop Ollama and restart it with this command:" code="OLLAMA_ORIGINS=* ollama serve" />
          ) : (
            <>
              <Step n={1} text="Open PowerShell and run this single command:" code={'$env:OLLAMA_ORIGINS="*"; ollama serve'} />
              <Step n={2}
                text="Or in Command Prompt (two steps):"
                sub="First run: set OLLAMA_ORIGINS=*   — then run: ollama serve"
              />
            </>
          )}
          <Step
            n={os !== 'win' ? 2 : 3}
            text="Then press Try Again below"
            sub="You should see a green \u201cRunning\u201d indicator."
          />
        </div>

        <button
          onClick={async () => { setRetrying(true); await probe(); setRetrying(false); }}
          disabled={retrying}
          style={{ width: '100%', padding: '12px', background: `${D.cyan}16`, border: `1px solid ${D.cyan}55`, borderRadius: 5, color: D.cyan, fontSize: 11, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, cursor: retrying ? 'wait' : 'pointer', letterSpacing: '.05em' }}
        >
          {retrying ? '\u27f3 Checking...' : '\u21bb Try Again'}
        </button>

        <div style={{ marginTop: 14, padding: '10px 12px', background: `${D.cyan}07`, border: `1px solid ${D.cyan}20`, borderRadius: 4, fontSize: 9, color: D.t4, lineHeight: 1.65 }}>
          {OLLAMA_IS_REMOTE
            ? <>Running on the <strong style={{ color: D.t2 }}>remote machine</strong>? Run that command there, not on your laptop.</>
            : <>Your Ollama is running but the browser is blocked at the OS level. The command above re-launches it with explicit browser permission.</>
          }
        </div>
      </div>
    );
  }

  // ────────────────────────────── CHECKING
  if (phase === 'checking') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: 12, padding: 24 }}>
        <div style={{ width: 28, height: 28, border: `2px solid ${D.cyan}55`, borderTopColor: D.cyan, borderRadius: '50%', animation: 'robot-float 0.8s linear infinite' }} />
        <div style={{ fontSize: 11, color: D.t3 }}>Connecting to Ollama...</div>
        <div style={{ fontSize: 9, color: D.t4, fontFamily: 'JetBrains Mono,monospace' }}>{OLLAMA_BASE}</div>
      </div>
    );
  }

  // ────────────────────────────── CONNECTED + MODELS (chat)
  if (phase === 'connected' && ollamaModels.length > 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <div style={{ padding: '8px 12px', borderBottom: `1px solid ${D.b0}`, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <div style={{ width: 7, height: 7, borderRadius: '50%', background: D.green, boxShadow: `0 0 6px ${D.green}` }} />
              <span style={{ fontSize: 9, color: D.green, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>RUNNING</span>
              <span style={{ fontSize: 8.5, color: D.t4, fontFamily: 'JetBrains Mono,monospace' }}>· {ollamaModels.length} model{ollamaModels.length !== 1 ? 's' : ''} ready</span>
            </div>
            <button onClick={probe} title="Refresh" style={{ background: 'none', border: `1px solid ${D.b1}`, color: D.t4, cursor: 'pointer', padding: '2px 6px', borderRadius: 2, fontSize: 10 }}>↻</button>
          </div>
        </div>
        <div style={{ flex: 1, minHeight: 0 }}>
          <OllamaChat installedModels={ollamaModels} onRefresh={probe} launchContext={launchContext} onClearLaunch={() => setLaunchContext(null)} />
        </div>
      </div>
    );
  }

  // ────────────────────────────── CONNECTED, NO MODELS YET
  if (phase === 'connected') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto', padding: '22px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: D.green, boxShadow: `0 0 7px ${D.green}` }} />
          <span style={{ fontSize: 9.5, color: D.green, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>Ollama is running</span>
        </div>
        <div style={{ fontSize: 14, fontWeight: 700, color: D.t1, marginBottom: 6 }}>Now get a model</div>
        <div style={{ fontSize: 10, color: D.t3, marginBottom: 18, lineHeight: 1.7 }}>
          Open your Terminal and run one of these commands. Each one downloads a model to your machine:
        </div>
        {[
          { cmd: 'ollama pull qwen3:4b',        label: 'Qwen3 4B',         meta: '3 GB · best for most tasks',      color: D.cyan   },
          { cmd: 'ollama pull llama3.2:3b',      label: 'Llama 3.2 3B',     meta: '2 GB · fast responses',           color: D.violet },
          { cmd: 'ollama pull deepseek-r1:1.5b', label: 'DeepSeek R1 1.5B', meta: '1 GB · step-by-step reasoning',   color: D.green  },
        ].map(item => (
          <div key={item.cmd} style={{ marginBottom: 10, padding: '10px 12px', background: D.surface, border: `1px solid ${D.b1}`, borderLeft: `2px solid ${item.color}`, borderRadius: 4 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
              <span style={{ fontSize: 11, color: item.color, fontWeight: 700 }}>{item.label}</span>
              <span style={{ fontSize: 8.5, color: D.t4, fontFamily: 'JetBrains Mono,monospace' }}>{item.meta}</span>
            </div>
            <code style={{ fontSize: 9, color: D.cyan, fontFamily: 'JetBrains Mono,monospace', display: 'block', padding: '5px 9px', background: D.raised, borderRadius: 3 }}>{item.cmd}</code>
          </div>
        ))}
        <button onClick={probe} style={{ width: '100%', marginTop: 4, padding: '11px', background: `${D.cyan}12`, border: `1px solid ${D.cyan}44`, borderRadius: 4, color: D.cyan, fontSize: 10, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, cursor: 'pointer', letterSpacing: '.05em' }}>
          ↻ Done — Refresh model list
        </button>
      </div>
    );
  }

  // ────────────────────────────── OFFLINE
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflowY: 'auto', padding: '22px 18px' }}>

      {/* Headline — instant status read */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 22 }}>
        <div style={{ width: 10, height: 10, borderRadius: '50%', background: D.red, boxShadow: `0 0 8px ${D.red}60`, flexShrink: 0 }} />
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: D.t1, lineHeight: 1.1 }}>
            {OLLAMA_IS_REMOTE ? "Can't reach remote Ollama" : "Ollama isn't running"}
          </div>
          <div style={{ fontSize: 8.5, color: D.t4, marginTop: 3, fontFamily: 'JetBrains Mono,monospace', wordBreak: 'break-all' }}>
            {OLLAMA_IS_REMOTE ? OLLAMA_BASE : `detected: ${OS_LABEL[os]}`}
          </div>
        </div>
      </div>

      {OLLAMA_IS_REMOTE ? (
        /* ── REMOTE MODE — CORS / server-side issue ─────────────────────── */
        <>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 22 }}>
            <Step n={1}
              text="Make sure Ollama is still running on the remote machine"
              sub="The ngrok tunnel only works while Ollama is running on the other computer."
            />
            <Step n={2}
              text="The remote Ollama must be started with CORS enabled"
              sub="Without this, browsers are blocked from connecting. On the remote machine, stop Ollama and restart it with:"
              code="OLLAMA_ORIGINS=* ollama serve"
            />
            <Step n={3}
              text="On Windows (remote machine), use PowerShell:"
              code={'$env:OLLAMA_ORIGINS="*"; ollama serve'}
            />
          </div>

          <button
            onClick={async () => { setRetrying(true); await probe(); setRetrying(false); }}
            disabled={retrying}
            style={{ width: '100%', padding: '12px', background: `${D.cyan}16`, border: `1px solid ${D.cyan}55`, borderRadius: 5, color: D.cyan, fontSize: 11, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, cursor: retrying ? 'wait' : 'pointer', letterSpacing: '.05em' }}
          >
            {retrying ? '⟳ Checking...' : '↻ Try Again'}
          </button>
        </>
      ) : !showInstall ? (
        /* ── LOCAL MODE — not running ───────────────────────────────────── */
        <>
          <div style={{ fontSize: 10.5, color: D.t3, marginBottom: 18, lineHeight: 1.7 }}>
            Start Ollama, then press <strong style={{ color: D.t2 }}>Try Again</strong> below.
          </div>

          {/* OS-specific steps — only 2, laser-focused */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 22 }}>
            {os === 'mac' && <>
              <Step n={1} text="Look for the llama icon in your menu bar" sub="It's in the top-right corner of your screen. If it's there, Ollama is already running — just press Try Again." />
              <Step n={2} text="Don't see it? Open Terminal and run:" code="ollama serve" />
            </>}
            {os === 'win' && <>
              <Step n={1} text="Check your system tray (near the clock)" sub="Look for the llama icon. If it's there, press Try Again." />
              <Step n={2} text="Don't see it? Open PowerShell and run:" code="ollama serve" />
            </>}
            {os === 'linux' && <>
              <Step n={1} text="Start Ollama in your terminal:" code="ollama serve" />
              <Step n={2} text="Or if you used systemd:" code="sudo systemctl start ollama" />
            </>}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 16 }}>
            <button
              onClick={async () => { setRetrying(true); await probe(); setRetrying(false); }}
              disabled={retrying}
              style={{ padding: '12px', background: `${D.cyan}16`, border: `1px solid ${D.cyan}55`, borderRadius: 5, color: D.cyan, fontSize: 10.5, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, cursor: retrying ? 'wait' : 'pointer', letterSpacing: '.04em' }}
            >
              {retrying ? '⟳ Checking...' : '↻ Try Again'}
            </button>
            <button
              onClick={() => setShowInstall(true)}
              style={{ padding: '12px', background: `${D.violet}12`, border: `1px solid ${D.violet}44`, borderRadius: 5, color: D.violet, fontSize: 10.5, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, cursor: 'pointer', letterSpacing: '.04em' }}
            >
              📖 Guide me
            </button>
          </div>

          {/* CORS — framed as secondary help, not a scary warning */}
          <div style={{ padding: '11px 13px', background: `${D.gold}08`, border: `1px solid ${D.gold}28`, borderRadius: 4 }}>
            <div style={{ fontSize: 10, color: D.gold, fontWeight: 700, marginBottom: 5 }}>Still not connecting?</div>
            <div style={{ fontSize: 9.5, color: D.t4, lineHeight: 1.65, marginBottom: 8 }}>
              Web browsers need explicit permission to talk to Ollama.
              Stop it and restart with this command instead:
            </div>
            <code style={{ display: 'block', fontSize: 9, color: D.cyan, background: D.raised, padding: '6px 10px', borderRadius: 3, fontFamily: 'JetBrains Mono,monospace', wordBreak: 'break-all' }}>OLLAMA_ORIGINS=* ollama serve</code>
          </div>
        </>
      ) : (
        <>
          {/* Install guide — shown only when user explicitly asks */}
          <button
            onClick={() => setShowInstall(false)}
            style={{ background: 'none', border: 'none', color: D.t4, cursor: 'pointer', fontSize: 10, padding: '0 0 14px', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 5 }}
          >← Back</button>

          <div style={{ fontSize: 14, fontWeight: 700, color: D.t1, marginBottom: 16 }}>Install Ollama</div>
          <div style={{ fontSize: 10, color: D.t3, marginBottom: 16, lineHeight: 1.65 }}>
            Ollama is a free app that runs AI models locally on your machine.
            No API keys, no internet needed after download.
          </div>

          {/* OS picker */}
          <div style={{ display: 'flex', gap: 4, marginBottom: 18 }}>
            {[['mac', '🍎 macOS'], ['win', '⊞ Windows'], ['linux', '🐧 Linux']].map(([id, label]) => (
              <button key={id} onClick={() => setOs(id)}
                style={{ flex: 1, padding: '7px 4px', background: os === id ? `${D.cyan}14` : 'transparent', border: `1px solid ${os === id ? D.cyan : D.b1}`, borderRadius: 3, color: os === id ? D.cyan : D.t4, fontSize: 8.5, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}
              >{label}</button>
            ))}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 18 }}>
            {(INSTALL_OS.find(o => o.id === os) || INSTALL_OS[0]).steps.map((step, i) => (
              <Step key={i} n={i + 1} text={step.desc} code={step.cmd} />
            ))}
          </div>

          <a href="https://ollama.com/download" target="_blank" rel="noopener noreferrer"
            style={{ display: 'block', padding: '12px', background: `${D.cyan}14`, border: `1px solid ${D.cyan}44`, borderRadius: 5, color: D.cyan, textAlign: 'center', textDecoration: 'none', fontSize: 10.5, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, letterSpacing: '.06em', marginBottom: 14 }}
          >DOWNLOAD OLLAMA ↗</a>

          <button
            onClick={async () => { setRetrying(true); await probe(); setRetrying(false); }}
            disabled={retrying}
            style={{ width: '100%', padding: '11px', background: `${D.green}10`, border: `1px solid ${D.green}40`, borderRadius: 5, color: D.green, fontSize: 10, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, cursor: retrying ? 'wait' : 'pointer', letterSpacing: '.04em' }}
          >
            {retrying ? '⟳ Checking...' : "↻ I've installed it — Test connection"}
          </button>
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ROOT
// ═══════════════════════════════════════════════════════════════════════════════
export default function LocalAgents() {
  const [tier,       setTier]       = useState(detectTier);
  const [agentBrains,setAgentBrains]= useState({});
  const [brainModal, setBrainModal] = useState(null);
  const [view,       setView]       = useState('agents');
  const [configAgent,setConfigAgent]= useState(null);
  const [ollamaOpen, setOllamaOpen] = useState(false);

  // Auto-open popup when an agent card fires LAUNCH
  useEffect(() => {
    const handler = () => setOllamaOpen(true);
    window.addEventListener('ollama-launch-agent', handler);
    return () => window.removeEventListener('ollama-launch-agent', handler);
  }, []);

  const handleAssign = useCallback((agentId, modelId) => {
    setAgentBrains(p => ({ ...p, [agentId]: modelId }));
  }, []);

  const T       = TIERS[tier];
  const armed   = Object.values(agentBrains).filter(Boolean).length;
  const compat  = MODELS.filter(m => m.tiers.includes(tier)).length;

  return (
    <div style={{ fontFamily: 'system-ui,sans-serif', color: D.t1, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <style>{`
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 4px; height: 4px; }
        ::-webkit-scrollbar-thumb { background: ${D.b2}; border-radius: 3px; }
      `}</style>

      {/* HEADER */}
      <div style={{ padding: '28px 28px 22px', borderBottom: `1px solid ${D.b0}`, flexShrink: 0 }}>
        <div style={{ fontSize: 8.5, color: T.color, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, letterSpacing: '.26em', marginBottom: 10 }}>
          ai.degenden.xyz · LOCAL AI AGENTS
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 24, alignItems: 'flex-start', marginBottom: 22 }}>
          <div>
            <h1 style={{ fontFamily: 'Syne,sans-serif', fontWeight: 900, fontSize: 34, color: D.t1, lineHeight: 1.1, margin: '0 0 12px' }}>
              Local AI Agents
              <br />
              <span style={{ background: `linear-gradient(90deg,${T.color},#a78bfa)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                On-Device Intelligence.
              </span>
            </h1>
            <p style={{ fontSize: 11.5, color: D.t3, maxWidth: 600, lineHeight: 1.85, margin: 0 }}>
              Download open-source LLMs directly to your machine. Assign them as reasoning brains
              to specialist AI agents — Data Analyst, ML Engineer, Research Assistant, Code Reviewer.
              Chain agents into automated workflows for end-to-end tasks.
              <strong style={{ color: D.t1 }}> No API keys. No data leaves your device. No monthly bill.</strong>
            </p>
          </div>

          <div style={{ padding: '18px 22px', background: D.surface, border: `1px solid ${D.b1}`, borderLeft: `3px solid ${T.color}`, borderRadius: 5, minWidth: 220 }}>
            <div style={{ fontSize: 7.5, color: D.t4, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.15em', marginBottom: 5 }}>SYSTEM STATUS</div>
            <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 900, fontSize: 20, color: T.color, marginBottom: 3 }}>{T.label} TIER</div>
            <div style={{ fontSize: 8.5, color: D.t3, fontFamily: 'JetBrains Mono,monospace', marginBottom: 14 }}>{T.headline} · {T.speedRange}</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, textAlign: 'center' }}>
              {[[armed, 'Armed', D.green], [compat, 'Compatible', T.color], [AGENTS.length, 'Agents', D.violet]].map(([v, l, c]) => (
                <div key={l}>
                  <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 900, fontSize: 20, color: c }}>{v}</div>
                  <div style={{ fontSize: 7.5, color: D.t4, fontFamily: 'JetBrains Mono,monospace', textTransform: 'uppercase' }}>{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ display: 'flex', gap: 2, background: D.deep, border: `1px solid ${D.b0}`, borderRadius: 4, padding: 3 }}>
            {[['agents','🤖 Agents'],['all','⬟ Full Page'],['hardware','🖥 Hardware'],['models','⬡ Models']].map(([id, lbl]) => (
              <button key={id} onClick={() => setView(id)}
                style={{ padding: '7px 16px', border: 'none', borderRadius: 3, cursor: 'pointer', background: view === id ? T.color : 'transparent', color: view === id ? D.void : D.t3, fontFamily: 'JetBrains Mono,monospace', fontSize: 9, fontWeight: 700, letterSpacing: '.08em', transition: 'all .15s' }}>
                {lbl}
              </button>
            ))}
          </div>
          <button
            onClick={() => setOllamaOpen(true)}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: `${D.cyan}18`, border: `1px solid ${D.cyan}55`, borderRadius: 4, color: D.cyan, fontFamily: 'JetBrains Mono,monospace', fontSize: 9, fontWeight: 700, letterSpacing: '.1em', cursor: 'pointer', transition: 'all .15s' }}
          >
            <span style={{ fontSize: 11 }}>&#9679;</span> OLLAMA
          </button>
        </div>
      </div>

      {/* BODY */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '32px 28px 80px' }}>
        {(view === 'all' || view === 'hardware') && <HardwareSection tier={tier} setTier={setTier} />}
        {(view === 'all' || view === 'models')   && <ModelLibrary tier={tier} agentBrains={agentBrains} onAssign={id => setBrainModal(id)} />}
        {(view === 'all' || view === 'agents')   && <AgentRoster tier={tier} agentBrains={agentBrains} setAgentBrains={setAgentBrains} onConfigure={setConfigAgent} />}
      </div>

      {/* OLLAMA POPUP */}
      {ollamaOpen && (
        <div
          onClick={() => setOllamaOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 200,
            background: 'rgba(2,5,8,.82)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 20,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              width: '100%', maxWidth: 540,
              maxHeight: '88vh',
              background: D.void,
              border: `1px solid ${D.b1}`,
              borderTop: `2px solid ${D.cyan}`,
              borderRadius: 8,
              overflow: 'hidden',
              display: 'flex', flexDirection: 'column',
              boxShadow: '0 24px 80px rgba(0,0,0,.6)',
              animation: 'authSlideUp .22s cubic-bezier(.16,1,.3,1)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', borderBottom: `1px solid ${D.b0}`, flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 10, color: D.cyan }}>&#9679;</span>
                <span style={{ fontSize: 8, color: D.t4, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.18em', fontWeight: 700 }}>OLLAMA · LOCAL ENGINE</span>
              </div>
              <button
                onClick={() => setOllamaOpen(false)}
                style={{ background: 'none', border: `1px solid ${D.b1}`, color: D.t3, cursor: 'pointer', fontSize: 15, lineHeight: 1, padding: '3px 7px', borderRadius: 3, fontFamily: 'JetBrains Mono,monospace' }}
                aria-label="Close Ollama panel"
              >
                ×
              </button>
            </div>
            <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
              <OllamaPanel onClose={() => setOllamaOpen(false)} />
            </div>
          </div>
        </div>
      )}

      {brainModal && <BrainModal modelId={brainModal} agentBrains={agentBrains} onAssign={handleAssign} onClose={() => setBrainModal(null)} />}
      {configAgent && <AgentConfigStudio agent={configAgent} tier={tier} agentBrains={agentBrains} setAgentBrains={setAgentBrains} onClose={() => setConfigAgent(null)} />}
    </div>
  );
}